import React, { useState, useEffect } from 'react';
import { LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';
import * as XY from '@xyflow/react';
import '@xyflow/react/dist/style.css';

const API = 'https://flow.wasavo.com/api';
(function(){ if (typeof window==='undefined' || window.__wasavoFetchPatched) return; window.__wasavoFetchPatched = true; const _f = window.fetch.bind(window); window.fetch = function(url, opts){ opts = opts || {}; try { const u = (typeof url==='string') ? url : (url && url.url) || ''; if (u.indexOf(API) === 0) { const tok = localStorage.getItem('wasavo_token'); if (tok) { const h = new Headers(opts.headers || {}); if (!h.has('Authorization')) h.set('Authorization', 'Bearer ' + tok); opts.headers = h; } } } catch(e){} return _f(url, opts); }; })();

function FlowBuilder({ flow, API, clientId, onBack, onSaved }) {
  const [name, setName] = React.useState(flow.name || 'Untitled Flow');
  const [triggerType, setTriggerType] = React.useState(flow.trigger_type || 'keyword');
  const [triggerValue, setTriggerValue] = React.useState(flow.trigger_value || '');
  const [nodes, setNodes] = React.useState([]);
  const [team, setTeam] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [savedMsg, setSavedMsg] = React.useState('');

  React.useEffect(() => {
    fetch(API + '/flows/' + flow.id + '?clientId=' + clientId)
      .then(r => r.json())
      .then(d => {
        var def = d.definition || { nodes: [], edges: [] };
        if (typeof def === 'string') { try { def = JSON.parse(def); } catch(e) { def = { nodes: [], edges: [] }; } }
        var ns = (def.nodes || []).filter(function(n){ return n.type !== 'start'; });
        setNodes(ns);
        setLoading(false);
      })
      .catch(function(){ setLoading(false); });
    fetch(API + '/team-members?clientId=' + clientId)
      .then(r => r.json())
      .then(d => { if (d.success) setTeam(d.members || []); })
      .catch(function(){});
  }, [flow.id]);

  var TYPES = [
    { type:'message',  label:'Message',   desc:'Send text, auto-advance' },
    { type:'question', label:'Question',  desc:'Ask, wait, save to variable' },
    { type:'buttons',  label:'Buttons',   desc:'Ask, wait, branch (max 3)' },
    { type:'condition',label:'Condition', desc:'If/else on a variable' },
    { type:'assign',   label:'Assign',    desc:'Hand off to team member' },
    { type:'template', label:'Template',  desc:'Send approved template' },
    { type:'end',      label:'End',       desc:'Stop the flow' }
  ];

  function newId(type) {
    var prefix = { message:'msg', question:'qst', buttons:'btn', condition:'cnd', assign:'asg', template:'tpl', end:'end' }[type] || 'nd';
    var max = 0;
    nodes.forEach(function(n){
      var m = (n.id||'').match(/_(\d+)$/);
      if (m && parseInt(m[1]) > max) max = parseInt(m[1]);
    });
    return prefix + '_' + (max + 1);
  }

  function defaultData(type) {
    if (type==='message')   return { text:'' };
    if (type==='question')  return { text:'', variable:'', validation:'none' };
    if (type==='buttons')   return { text:'', buttons:[{ id:'b1', label:'' }] };
    if (type==='condition') return { variable:'', operator:'equals', value:'' };
    if (type==='assign')    return { assignTo:'', stopFlow:false };
    if (type==='template')  return { templateName:'', language:'en_US', variables:[] };
    return {};
  }

  function addNode(type) {
    var n = { id:newId(type), type:type, position:{ x:0, y:nodes.length*120 }, data:defaultData(type) };
    setNodes(nodes.concat([n]));
    setEditing(n.id);
  }

  function updateNode(id, data) {
    setNodes(nodes.map(function(n){ return n.id===id ? Object.assign({}, n, { data:data }) : n; }));
  }

  function deleteNode(id) {
    if (!window.confirm('Delete this block?')) return;
    setNodes(nodes.filter(function(n){ return n.id!==id; }));
    if (editing===id) setEditing(null);
  }

  function moveNode(idx, dir) {
    var j = idx + dir;
    if (j < 0 || j >= nodes.length) return;
    var arr = nodes.slice();
    var tmp = arr[idx]; arr[idx] = arr[j]; arr[j] = tmp;
    setNodes(arr);
  }

  function buildDefinition() {
    var startNode = { id:'start', type:'start', position:{x:0,y:0}, data:{} };
    var all = [startNode].concat(nodes);
    var extra = [];
    var edges = [];
    for (var i=0; i<all.length; i++) {
      var src = all[i];
      var tgt = all[i+1] || null;
      if (src.type==='buttons') {
        (src.data.buttons||[]).forEach(function(b){
          if (b.reply && String(b.reply).trim()) {
            var rid = 'msg_' + src.id + '_' + b.id;
            var eid = 'end_' + src.id + '_' + b.id;
            extra.push({ id:rid, type:'message', position:{x:0,y:0}, data:{ text:b.reply } });
            extra.push({ id:eid, type:'end', position:{x:0,y:0}, data:{} });
            edges.push({ id:'e_'+src.id+'_'+b.id, source:src.id, sourceHandle:b.id, target:rid });
            edges.push({ id:'e_'+rid, source:rid, sourceHandle:null, target:eid });
          } else if (tgt) {
            edges.push({ id:'e_'+src.id+'_'+b.id, source:src.id, sourceHandle:b.id, target:tgt.id });
          }
        });
        if (tgt) edges.push({ id:'e_'+src.id+'_fallback', source:src.id, sourceHandle:'fallback', target:tgt.id });
      } else if (src.type==='condition') {
        if (tgt) {
          edges.push({ id:'e_'+src.id+'_true', source:src.id, sourceHandle:'true', target:tgt.id });
          edges.push({ id:'e_'+src.id+'_false', source:src.id, sourceHandle:'false', target:tgt.id });
        }
      } else if (src.type!=='end') {
        if (tgt) edges.push({ id:'e_'+src.id, source:src.id, sourceHandle:null, target:tgt.id });
      }
    }
    return { nodes:all.concat(extra), edges:edges };
  }

  function save() {
    setSaving(true); setSavedMsg('');
    var def = buildDefinition();
    fetch(API + '/flows/' + flow.id, {
      method:'PUT',
      headers:{ 'Content-Type':'application/json' },
      body:JSON.stringify({ clientId:clientId, name:name, trigger_type:triggerType, trigger_value:triggerValue, definition:def })
    })
      .then(r => r.json())
      .then(d => {
        setSaving(false);
        if (d && d.id) { setSavedMsg('Saved'); if (onSaved) onSaved(d); setTimeout(function(){ setSavedMsg(''); }, 2000); }
        else setSavedMsg('Error: ' + (d.error || 'unknown'));
      })
      .catch(function(e){ setSaving(false); setSavedMsg('Error: ' + e.message); });
  }

  var c = {
    wrap:{},
    bar:{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'18px', flexWrap:'wrap' },
    back:{ padding:'8px 14px', background:'#f1f5f9', color:'#0f172a', border:'1px solid #e2e8f0', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600' },
    nameInput:{ flex:1, minWidth:'180px', padding:'9px 12px', border:'1px solid #e2e8f0', borderRadius:'8px', fontSize:'15px', fontWeight:'600', color:'#0f172a', background:'#fff' },
    saveBtn:{ padding:'9px 20px', background:'#16a34a', color:'#fff', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600' },
    card:{ background:'#fff', borderRadius:'12px', padding:'18px', border:'1px solid #ecedf2', marginBottom:'16px' },
    label:{ fontSize:'12px', fontWeight:'600', color:'#64748b', marginBottom:'6px', display:'block' },
    input:{ width:'100%', padding:'9px 12px', border:'1px solid #e2e8f0', borderRadius:'8px', fontSize:'13px', boxSizing:'border-box', color:'#0f172a', background:'#fff', marginBottom:'10px' },
    blockRow:{ border:'1px solid #e8edeb', borderRadius:'10px', padding:'12px 14px', marginBottom:'10px', background:'#fbfcfb' },
    blockHead:{ display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer' },
    badge:(bg,fg)=>({ display:'inline-block', padding:'2px 9px', borderRadius:'6px', fontSize:'11px', fontWeight:'700', background:bg, color:fg, marginRight:'8px', textTransform:'uppercase', letterSpacing:'0.3px' }),
    smallBtn:{ padding:'4px 9px', background:'#fff', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer', fontSize:'12px', color:'#475569', marginLeft:'4px' },
    delBtn:{ padding:'4px 9px', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'6px', cursor:'pointer', fontSize:'12px', color:'#dc2626', marginLeft:'4px' },
    addGrid:{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:'8px' },
    addBtn:{ textAlign:'left', padding:'10px 12px', border:'1px dashed #cbd5e1', borderRadius:'10px', cursor:'pointer', background:'#fff' },
    empty:{ textAlign:'center', padding:'30px', color:'#94a3b8', fontSize:'13px' }
  };

  var COLORS = {
    message:['#dbeafe','#1e40af'], question:['#ede9fe','#6d28d9'], buttons:['#fce7f3','#be185d'],
    condition:['#fef3c7','#b45309'], assign:['#dcfce7','#15803d'], template:['#cffafe','#0e7490'], end:['#f1f5f9','#475569']
  };

  function summary(n) {
    var d = n.data || {};
    if (n.type==='message')   return d.text || '(empty message)';
    if (n.type==='question')  return (d.text || '(no question)') + (d.variable ? '  →  {{'+d.variable+'}}' : '');
    if (n.type==='buttons')   return (d.text || '(no prompt)') + '  ['+(d.buttons||[]).map(function(b){return b.label||'?';}).join(', ')+']';
    if (n.type==='condition') return 'if {{'+(d.variable||'?')+'}} '+(d.operator||'equals')+' "'+(d.value||'')+'"';
    if (n.type==='assign')    { var m=team.filter(function(t){return String(t.id)===String(d.assignTo);})[0]; return 'Assign to '+(m?m.name:'(pick agent)')+(d.stopFlow?' · stop flow':''); }
    if (n.type==='template')  return d.templateName ? ('Template: '+d.templateName) : '(no template)';
    if (n.type==='end')       return 'Flow ends here';
    return '';
  }

  function Editor(n) {
    var d = n.data || {};
    if (n.type==='message') return (
      React.createElement('div', null,
        React.createElement('label', { style:c.label }, 'Message text  (use {{variable}} to insert saved values)'),
        React.createElement('textarea', { style:Object.assign({},c.input,{minHeight:'70px',fontFamily:'inherit'}), value:d.text||'', onChange:function(e){updateNode(n.id,Object.assign({},d,{text:e.target.value}));} })
      )
    );
    if (n.type==='question') return (
      React.createElement('div', null,
        React.createElement('label', { style:c.label }, 'Question to ask'),
        React.createElement('textarea', { style:Object.assign({},c.input,{minHeight:'56px',fontFamily:'inherit'}), value:d.text||'', onChange:function(e){updateNode(n.id,Object.assign({},d,{text:e.target.value}));} }),
        React.createElement('label', { style:c.label }, 'Save answer to variable name'),
        React.createElement('input', { style:c.input, placeholder:'e.g. customer_name', value:d.variable||'', onChange:function(e){updateNode(n.id,Object.assign({},d,{variable:e.target.value.replace(/[^a-zA-Z0-9_]/g,'_')}));} })
      )
    );
    if (n.type==='buttons') {
      var btns = d.buttons || [];
      return (
        React.createElement('div', null,
          React.createElement('label', { style:c.label }, 'Prompt text'),
          React.createElement('textarea', { style:Object.assign({},c.input,{minHeight:'50px',fontFamily:'inherit'}), value:d.text||'', onChange:function(e){updateNode(n.id,Object.assign({},d,{text:e.target.value}));} }),
          React.createElement('label', { style:c.label }, 'Buttons (max 3)'),
          btns.map(function(b,i){
            return React.createElement('div', { key:i, style:{display:'flex',gap:'6px',marginBottom:'8px'} },
              React.createElement('input', { style:Object.assign({},c.input,{marginBottom:0}), placeholder:'Button '+(i+1)+' label', value:b.label||'', onChange:function(e){ var nb=btns.slice(); nb[i]=Object.assign({},b,{label:e.target.value}); updateNode(n.id,Object.assign({},d,{buttons:nb})); } }),
              React.createElement('button', { style:c.delBtn, onClick:function(){ var nb=btns.filter(function(_,j){return j!==i;}); updateNode(n.id,Object.assign({},d,{buttons:nb})); } }, '×')
            );
          }),
          React.createElement('div', { style:{height:'4px'} }),
          btns.map(function(b,i){
            return React.createElement('div', { key:'r'+i, style:{marginBottom:'10px'} },
              React.createElement('label', { style:Object.assign({},c.label,{fontSize:'11px'}) }, 'Reply when "'+(b.label||('Button '+(i+1)))+'" is clicked'),
              React.createElement('textarea', { style:Object.assign({},c.input,{minHeight:'44px',fontFamily:'inherit',marginBottom:0}), placeholder:'Leave empty to just continue the flow', value:b.reply||'', onChange:function(e){ var nb=btns.slice(); nb[i]=Object.assign({},b,{reply:e.target.value}); updateNode(n.id,Object.assign({},d,{buttons:nb})); } })
            );
          }),
          btns.length<3 ? React.createElement('button', { style:c.smallBtn, onClick:function(){ var nb=btns.concat([{id:'b'+(btns.length+1),label:''}]); updateNode(n.id,Object.assign({},d,{buttons:nb})); } }, '+ Add button') : null
        )
      );
    }
    if (n.type==='condition') return (
      React.createElement('div', null,
        React.createElement('label', { style:c.label }, 'Variable to check'),
        React.createElement('input', { style:c.input, placeholder:'e.g. customer_name', value:d.variable||'', onChange:function(e){updateNode(n.id,Object.assign({},d,{variable:e.target.value.replace(/[^a-zA-Z0-9_]/g,'_')}));} }),
        React.createElement('label', { style:c.label }, 'Operator'),
        React.createElement('select', { style:c.input, value:d.operator||'equals', onChange:function(e){updateNode(n.id,Object.assign({},d,{operator:e.target.value}));} },
          ['equals','not_equals','contains','is_empty'].map(function(op){ return React.createElement('option', { key:op, value:op }, op); })
        ),
        d.operator!=='is_empty' ? React.createElement('div', null,
          React.createElement('label', { style:c.label }, 'Value'),
          React.createElement('input', { style:c.input, value:d.value||'', onChange:function(e){updateNode(n.id,Object.assign({},d,{value:e.target.value}));} })
        ) : null
      )
    );
    if (n.type==='assign') return (
      React.createElement('div', null,
        React.createElement('label', { style:c.label }, 'Assign conversation to'),
        React.createElement('select', { style:c.input, value:d.assignTo||'', onChange:function(e){updateNode(n.id,Object.assign({},d,{assignTo:e.target.value}));} },
          [React.createElement('option', { key:'', value:'' }, '— pick team member —')].concat(
            team.map(function(t){ return React.createElement('option', { key:t.id, value:t.id }, t.name+' ('+t.role+')'); })
          )
        ),
        React.createElement('label', { style:{display:'flex',alignItems:'center',gap:'8px',fontSize:'13px',color:'#475569',cursor:'pointer'} },
          React.createElement('input', { type:'checkbox', checked:!!d.stopFlow, onChange:function(e){updateNode(n.id,Object.assign({},d,{stopFlow:e.target.checked}));} }),
          'Stop the flow after assigning (hand fully to agent)'
        )
      )
    );
    if (n.type==='template') return (
      React.createElement('div', null,
        React.createElement('label', { style:c.label }, 'Template name (must be approved in Meta)'),
        React.createElement('input', { style:c.input, placeholder:'e.g. order_update', value:d.templateName||'', onChange:function(e){updateNode(n.id,Object.assign({},d,{templateName:e.target.value}));} }),
        React.createElement('label', { style:c.label }, 'Language code'),
        React.createElement('input', { style:c.input, placeholder:'en_US', value:d.language||'en_US', onChange:function(e){updateNode(n.id,Object.assign({},d,{language:e.target.value}));} })
      )
    );
    if (n.type==='end') return React.createElement('div', { style:{fontSize:'13px',color:'#64748b'} }, 'This block ends the conversation flow. No settings needed.');
    return null;
  }

  if (loading) return React.createElement('div', { style:c.empty }, 'Loading flow…');

  return (
    React.createElement('div', { style:c.wrap },
      React.createElement('div', { style:c.bar },
        React.createElement('button', { style:c.back, onClick:onBack }, '← Back'),
        React.createElement('input', { style:c.nameInput, value:name, onChange:function(e){setName(e.target.value);} }),
        savedMsg ? React.createElement('span', { style:{fontSize:'13px',fontWeight:'600',color:savedMsg==='Saved'?'#16a34a':'#dc2626'} }, savedMsg) : null,
        React.createElement('button', { style:c.saveBtn, disabled:saving, onClick:save }, saving?'Saving…':'Save flow')
      ),

      React.createElement('div', { style:c.card },
        React.createElement('label', { style:c.label }, 'Trigger — when does this flow start?'),
        React.createElement('div', { style:{display:'flex',gap:'8px',flexWrap:'wrap'} },
          React.createElement('select', { style:Object.assign({},c.input,{flex:'0 0 200px',marginBottom:0}), value:triggerType, onChange:function(e){setTriggerType(e.target.value);} },
            React.createElement('option', { value:'keyword' }, 'Keyword match'),
            React.createElement('option', { value:'any_message' }, 'Any message'),
            React.createElement('option', { value:'new_contact' }, 'New contact')
          ),
          triggerType==='keyword' ? React.createElement('input', { style:Object.assign({},c.input,{flex:1,marginBottom:0}), placeholder:'Keyword e.g. hi, menu, order', value:triggerValue, onChange:function(e){setTriggerValue(e.target.value);} }) : null
        )
      ),

      React.createElement('div', { style:c.card },
        React.createElement('div', { style:{fontSize:'14px',fontWeight:'700',color:'#0f172a',marginBottom:'12px'} }, 'Flow steps'),
        nodes.length===0 ? React.createElement('div', { style:c.empty }, 'No steps yet. Add a block below to begin.') : null,
        nodes.map(function(n,idx){
          var col = COLORS[n.type] || ['#f1f5f9','#475569'];
          var isOpen = editing===n.id;
          return React.createElement('div', { key:n.id, style:c.blockRow },
            React.createElement('div', { style:c.blockHead, onClick:function(){ setEditing(isOpen?null:n.id); } },
              React.createElement('div', { style:{flex:1,minWidth:0} },
                React.createElement('span', { style:c.badge(col[0],col[1]) }, n.type),
                React.createElement('span', { style:{fontSize:'13px',color:'#334155'} }, summary(n))
              ),
              React.createElement('div', { style:{display:'flex',alignItems:'center',flexShrink:0} },
                React.createElement('button', { style:c.smallBtn, onClick:function(e){e.stopPropagation();moveNode(idx,-1);} }, '↑'),
                React.createElement('button', { style:c.smallBtn, onClick:function(e){e.stopPropagation();moveNode(idx,1);} }, '↓'),
                React.createElement('button', { style:c.delBtn, onClick:function(e){e.stopPropagation();deleteNode(n.id);} }, 'Delete')
              )
            ),
            isOpen ? React.createElement('div', { style:{marginTop:'12px',paddingTop:'12px',borderTop:'1px solid #eef2f0'} }, Editor(n)) : null
          );
        })
      ),

      React.createElement('div', { style:c.card },
        React.createElement('div', { style:{fontSize:'14px',fontWeight:'700',color:'#0f172a',marginBottom:'12px'} }, 'Add a block'),
        React.createElement('div', { style:c.addGrid },
          TYPES.map(function(t){
            var col = COLORS[t.type] || ['#f1f5f9','#475569'];
            return React.createElement('button', { key:t.type, style:c.addBtn, onClick:function(){addNode(t.type);} },
              React.createElement('span', { style:c.badge(col[0],col[1]) }, t.label),
              React.createElement('div', { style:{fontSize:'11px',color:'#94a3b8',marginTop:'5px'} }, t.desc)
            );
          })
        )
      )
    )
  );
}



function CampaignDetails({ campaign, onClose, API, clientId }) {
  const [messages, setMessages] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const fetchMessages = () => {
    setLoading(true);
    fetch(API + '/campaign-messages?campaignId=' + campaign.id + '&clientId=' + clientId)
      .then(r => r.json())
      .then(d => { if(d.success) setMessages(d.messages); setLoading(false); })
      .catch(() => setLoading(false));
  };
  React.useEffect(() => { fetchMessages(); }, [campaign.id]);
  const sent = messages.filter(m=>m.status==='sent'||m.status==='delivered'||m.status==='read').length;
  const delivered = messages.filter(m=>m.status==='delivered'||m.status==='read').length;
  const read = messages.filter(m=>m.status==='read').length;
  const failed = messages.filter(m=>m.status==='failed').length;
  const actualSent = campaign.sent || sent;
  return (
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={onClose}>
      <div style={{background:'#fff',borderRadius:'16px',padding:'24px',width:'700px',maxHeight:'85vh',overflowY:'auto'}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'20px'}}>
          <div style={{fontSize:'18px',fontWeight:'700',color:'#1e1b4b'}}>{campaign.name}</div>
          <div style={{display:'flex',gap:'8px'}}>
            <button style={{background:'#22c55e',color:'#fff',border:'none',borderRadius:'8px',padding:'6px 14px',cursor:'pointer'}} onClick={fetchMessages}>Refresh</button>
            <button style={{background:'#6b7280',color:'#fff',border:'none',borderRadius:'8px',padding:'6px 14px',cursor:'pointer'}} onClick={onClose}>Close</button>
          </div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:'12px',marginBottom:'20px'}}>
          <div style={{background:'#f0fdf4',borderRadius:'10px',padding:'14px',textAlign:'center'}}>
            <div style={{fontSize:'22px',fontWeight:'700',color:'#22c55e'}}>{actualSent}</div>
            <div style={{fontSize:'11px',color:'#6b7280',marginTop:'4px'}}>Total Sent</div>
          </div>
          <div style={{background:'#f0fdf4',borderRadius:'10px',padding:'14px',textAlign:'center'}}>
            <div style={{fontSize:'22px',fontWeight:'700',color:'#16a34a'}}>{delivered}</div>
            <div style={{fontSize:'11px',color:'#6b7280',marginTop:'4px'}}>Delivered</div>
          </div>
          <div style={{background:'#fef3c7',borderRadius:'10px',padding:'14px',textAlign:'center'}}>
            <div style={{fontSize:'22px',fontWeight:'700',color:'#d97706'}}>{read}</div>
            <div style={{fontSize:'11px',color:'#6b7280',marginTop:'4px'}}>Read</div>
          </div>
          <div style={{background:'#fef2f2',borderRadius:'10px',padding:'14px',textAlign:'center'}}>
            <div style={{fontSize:'22px',fontWeight:'700',color:'#dc2626'}}>{failed}</div>
            <div style={{fontSize:'11px',color:'#6b7280',marginTop:'4px'}}>Failed</div>
          </div>
        </div>
        <div style={{fontSize:'12px',color:'#6b7280',marginBottom:'16px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
          <div><b>Template:</b> {campaign.template_name}</div>
          <div><b>Status:</b> {campaign.status}</div>
          <div><b>Date:</b> {campaign.created_at?new Date(campaign.created_at).toLocaleString():'-'}</div>
          <div><b>Scheduled:</b> {campaign.scheduled_at?new Date(campaign.scheduled_at).toLocaleString():'-'}</div>
        </div>
        <div style={{borderTop:'1px solid #e5e7eb',paddingTop:'16px'}}>
          <div style={{fontWeight:'600',marginBottom:'12px',color:'#374151',fontSize:'14px'}}>Per Contact Status</div>
          {loading?<div style={{textAlign:'center',padding:'20px',color:'#9ca3af'}}>Loading...</div>:
          messages.length>0?(
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
              <thead><tr style={{background:'#f9fafb'}}><th style={{padding:'8px',textAlign:'left',borderBottom:'1px solid #e5e7eb'}}>#</th><th style={{padding:'8px',textAlign:'left',borderBottom:'1px solid #e5e7eb'}}>Name</th><th style={{padding:'8px',textAlign:'left',borderBottom:'1px solid #e5e7eb'}}>Phone</th><th style={{padding:'8px',textAlign:'left',borderBottom:'1px solid #e5e7eb'}}>Sent At</th><th style={{padding:'8px',textAlign:'left',borderBottom:'1px solid #e5e7eb'}}>Status</th></tr></thead>
              <tbody>{messages.map((m,i)=>(
                <tr key={m.id} style={{borderBottom:'1px solid #f3f4f6'}}>
                  <td style={{padding:'8px',color:'#6b7280'}}>{i+1}</td>
                  <td style={{padding:'8px',fontWeight:'600'}}>{m.contact_name||m.name||'-'}</td>
                  <td style={{padding:'8px',color:'#6b7280'}}>{m.phone}</td>
                  <td style={{padding:'8px',color:'#6b7280'}}>{m.sent_at?new Date(m.sent_at).toLocaleString():'-'}</td>
                  <td style={{padding:'8px'}}><span style={{padding:'2px 8px',borderRadius:'12px',fontSize:'11px',fontWeight:'600',background:m.status==='failed'?'#fef2f2':m.status==='read'?'#f0fdf4':'#f0fdf4',color:m.status==='failed'?'#dc2626':m.status==='read'?'#16a34a':'#22c55e'}}>{m.status}</span></td>
                </tr>
              ))}</tbody>
            </table>
          ):<div style={{textAlign:'center',padding:'20px',color:'#9ca3af'}}>No contact data yet. Send a new campaign to see details.</div>}
        </div>
      </div>
    </div>
  );
}


const P = '#16a34a';
const DARK = '#1e2139';

function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);

  const handleLogin = async () => {
    if (!email || !password) { setError('Please enter email and password.'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) });
      const data = await res.json();
      if (data.success) { try { if(data.token) localStorage.setItem('wasavo_token', data.token); } catch(e){} onLogin(data.user); }
      else { setError(data.error || 'Login failed.'); }
    } catch(e) { setError('Cannot connect to server.'); }
    setLoading(false);
  };

  return (
    <div className="wl-root">
      <style>{`
        .wl-root{min-height:100vh;display:flex;align-items:center;justify-content:center;background:#eaf5ee;font-family:'Segoe UI',sans-serif;padding:20px;box-sizing:border-box;}
        .wl-shell{display:flex;width:100%;max-width:1080px;background:#fff;border-radius:24px;overflow:hidden;box-shadow:0 24px 70px rgba(16,44,28,0.12);border:1px solid #e6efe9;}
        .wl-left{flex:1;padding:46px 44px;background:linear-gradient(160deg,#f4faf6 0%,#e8f6ed 55%,#daf0e2 100%);display:flex;flex-direction:column;}
        .wl-right{width:430px;padding:48px 44px;display:flex;flex-direction:column;justify-content:center;box-sizing:border-box;}
        .wl-logo{width:172px;height:auto;margin-bottom:26px;}
        .wl-h1{font-size:31px;line-height:1.22;font-weight:800;color:#0f172a;margin:0 0 14px;}
        .wl-h1 .g{color:#16a34a;}
        .wl-sub{font-size:14px;color:#64748b;line-height:1.6;margin:0 0 26px;max-width:370px;}
        .wl-feat{display:flex;gap:13px;margin-bottom:15px;align-items:flex-start;}
        .wl-fic{width:40px;height:40px;border-radius:11px;background:#fff;border:1px solid #d9ecdf;display:flex;align-items:center;justify-content:center;flex-shrink:0;color:#16a34a;}
        .wl-ft{font-size:13px;font-weight:700;color:#0f172a;margin-bottom:2px;}
        .wl-fd{font-size:12px;color:#64748b;line-height:1.5;}
        .wl-cardlogo{width:86px;height:auto;display:block;margin:0 auto 18px;}
        .wl-title{font-size:24px;font-weight:800;color:#0f172a;text-align:center;margin:0 0 6px;}
        .wl-ctext{font-size:13px;color:#64748b;text-align:center;line-height:1.5;margin:0 0 26px;}
        .wl-lbl{font-size:13px;font-weight:700;color:#0f172a;display:block;margin-bottom:7px;}
        .wl-field{position:relative;margin-bottom:18px;}
        .wl-input{width:100%;padding:13px 14px 13px 44px;border:1px solid #e2e8f0;border-radius:10px;font-size:14px;box-sizing:border-box;color:#0f172a;outline:none;background:#fff;transition:border .15s,box-shadow .15s;}
        .wl-input:focus{border-color:#16a34a;box-shadow:0 0 0 3px rgba(22,163,74,0.13);}
        .wl-iico{position:absolute;left:14px;top:50%;transform:translateY(-50%);color:#94a3b8;display:flex;}
        .wl-eye{position:absolute;right:12px;top:50%;transform:translateY(-50%);color:#94a3b8;background:none;border:none;cursor:pointer;padding:4px;display:flex;}
        .wl-rowx{display:flex;align-items:center;justify-content:space-between;margin-bottom:22px;}
        .wl-rem{display:flex;align-items:center;gap:8px;font-size:13px;color:#475569;cursor:pointer;user-select:none;}
        .wl-cb{width:18px;height:18px;border-radius:5px;border:1.5px solid #cbd5e1;display:flex;align-items:center;justify-content:center;background:#fff;color:#fff;flex-shrink:0;}
        .wl-cb.on{background:#16a34a;border-color:#16a34a;}
        .wl-forgot{font-size:13px;color:#16a34a;font-weight:600;cursor:pointer;}
        .wl-btn{width:100%;padding:14px;background:#16a34a;color:#fff;border:none;border-radius:10px;font-size:15px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;transition:background .15s;}
        .wl-btn:hover{background:#15803d;}
        .wl-btn:disabled{opacity:.7;cursor:default;}
        .wl-err{background:#fef2f2;color:#991b1b;padding:10px 14px;border-radius:9px;font-size:13px;margin-bottom:16px;}
        .wl-foot{text-align:center;margin-top:24px;font-size:12px;color:#94a3b8;}
        @media(max-width:840px){.wl-left{display:none;}.wl-right{width:100%;}}
      `}</style>
      <div className="wl-shell">
        <div className="wl-left">
          <img className="wl-logo" src={process.env.PUBLIC_URL + '/logo-login.png'} alt="Wasavo"/>
          <h1 className="wl-h1">Build <span className="g">meaningful</span> relationships on WhatsApp that drive lasting <span className="g">growth.</span></h1>
          <p className="wl-sub">Automate conversations, engage customers, and grow your business with the power of WhatsApp.</p>
          {[
            {t:'Smart Conversations',d:'Engage in meaningful conversations that convert.',ic:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.4 8.4 0 0 1-9 8.5 8.5 8.5 0 0 1-3.8-.9L3 20l1.9-5.2A8.5 8.5 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z"/></svg>},
            {t:'Intelligent Automation',d:'Automate workflows and save time while you scale.',ic:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>},
            {t:'Growth That Lasts',d:'Data-driven insights that help you grow repeat business.',ic:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="m7 14 4-4 3 3 5-6"/></svg>},
            {t:'Team Collaboration',d:'Collaborate with your team and serve customers better.',ic:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>},
          ].map((f,i)=>(
            <div className="wl-feat" key={i}>
              <div className="wl-fic">{f.ic}</div>
              <div><div className="wl-ft">{f.t}</div><div className="wl-fd">{f.d}</div></div>
            </div>
          ))}
        </div>
        <div className="wl-right">
          <img className="wl-cardlogo" src={process.env.PUBLIC_URL + '/logo-login.png'} alt="Wasavo"/>
          <h2 className="wl-title">Welcome back</h2>
          <p className="wl-ctext">Log in to your Wasavo account to continue</p>
          {error && <div className="wl-err">{error}</div>}
          <label className="wl-lbl">Email address</label>
          <div className="wl-field">
            <span className="wl-iico"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg></span>
            <input className="wl-input" placeholder="Enter your email" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleLogin()}/>
          </div>
          <label className="wl-lbl">Password</label>
          <div className="wl-field">
            <span className="wl-iico"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg></span>
            <input className="wl-input" type={showPw?'text':'password'} placeholder="Enter your password" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleLogin()}/>
            <button type="button" className="wl-eye" onClick={()=>setShowPw(!showPw)}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg></button>
          </div>
          <div className="wl-rowx">
            <div className="wl-rem" onClick={()=>setRemember(!remember)}>
              <span className={remember?'wl-cb on':'wl-cb'}>{remember&&<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5 9-11"/></svg>}</span>
              Remember me
            </div>
            <span className="wl-forgot">Forgot password?</span>
          </div>
          <button className="wl-btn" onClick={handleLogin} disabled={loading}>{loading?'Logging in...':'Login'}{!loading&&<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>}</button>
          <div className="wl-foot">Powered by Wasavo Flow</div>
        </div>
      </div>
    </div>
  );
}

// ===== FLOW CANVAS (visual builder, ReactFlow) =====
const FC_XY = XY;
const { ReactFlow: FC_ReactFlow, Background: FC_Background, Controls: FC_Controls, MiniMap: FC_MiniMap, Handle: FC_Handle, useNodesState: FC_useNodesState, useEdgesState: FC_useEdgesState, addEdge: FC_addEdge, EdgeLabelRenderer: FC_EdgeLabelRenderer, BaseEdge: FC_BaseEdge } = FC_XY;

const FC_H_GAP = 340, FC_V_GAP = 160;
function FC_computeLayout(def){
  const ch = {};
  def.edges.forEach(e => { (ch[e.source] = ch[e.source] || []).push(e.target); });
  const startN = def.nodes.find(n => n.type === 'start') || def.nodes[0];
  const startId = startN && startN.id;
  const depth = {};
  if (startId){ depth[startId] = 0; const q=[startId];
    while(q.length){ const c=q.shift(); (ch[c]||[]).forEach(t=>{ if(depth[t]===undefined){depth[t]=depth[c]+1; q.push(t);} }); }
  }
  def.nodes.forEach(n=>{ if(depth[n.id]===undefined) depth[n.id]=0; });
  const yy={}, seen=new Set(); let slot=0;
  function dfs(id){
    if(seen.has(id)) return yy[id];
    seen.add(id);
    const kids=(ch[id]||[]).filter(k=>!seen.has(k));
    if(!kids.length){ yy[id]=slot*FC_V_GAP; slot+=1; return yy[id]; }
    const ys=kids.map(dfs); yy[id]=ys.reduce((a,b)=>a+b,0)/ys.length; return yy[id];
  }
  if(startId) dfs(startId);
  def.nodes.forEach(n=>{ if(!seen.has(n.id)) dfs(n.id); });
  const pos={}; def.nodes.forEach(n=>{ pos[n.id]={ x:(depth[n.id]||0)*FC_H_GAP, y:yy[n.id]||0 }; });
  return pos;
}

const FC_C = { start:'#4f46e5', end:'#16a34a', message:'#0ea5e9', buttons:'#f59e0b', question:'#14b8a6', condition:'#eab308', assign:'#64748b', template:'#8b5cf6' };
function FC_box(type, width){
  return { background:FC_C[type]||'#334155', color: type==='condition'?'#111':'#fff', borderRadius:10, padding:'10px 14px', fontSize:12, width:width||200, boxShadow:'0 1px 4px rgba(0,0,0,0.25)', textAlign:'left' };
}
const FC_head = { fontSize:10, fontWeight:700, letterSpacing:0.5, opacity:0.85 };
const FC_dot = { width:9, height:9, background:'#fff', border:'2px solid #475569' };

function FC_StartNode(){
  return React.createElement('div', { style: FC_box('start',140) },
    React.createElement('div', { style: FC_head }, 'START'),
    React.createElement(FC_Handle, { type:'source', position:'right', style: FC_dot })
  );
}
function FC_EndNode(){
  return React.createElement('div', { style: FC_box('end',140) },
    React.createElement(FC_Handle, { type:'target', position:'left', style: FC_dot }),
    React.createElement('div', { style: FC_head }, 'END')
  );
}
function FC_SimpleNode(props){
  const data = props.data;
  return React.createElement('div', { style: FC_box(data._type) },
    React.createElement(FC_Handle, { type:'target', position:'left', style: FC_dot }),
    React.createElement('div', { style: FC_head }, (data._type||'').toUpperCase()),
    data.text ? React.createElement('div', { style:{ marginTop:4, lineHeight:1.3 } }, data.text) : null,
    React.createElement(FC_Handle, { type:'source', position:'right', style: FC_dot })
  );
}
function FC_ButtonsNode(props){
  const data = props.data;
  const btns = data.buttons || [];
  return React.createElement('div', { style: FC_box('buttons',220) },
    React.createElement(FC_Handle, { type:'target', position:'left', style: FC_dot }),
    React.createElement('div', { style: FC_head }, 'BUTTONS'),
    data.text ? React.createElement('div', { style:{ marginTop:4, lineHeight:1.3 } }, data.text) : null,
    React.createElement('div', { style:{ marginTop:8 } },
      btns.map(b => React.createElement('div', { key:b.id, style:{ position:'relative', padding:'6px 10px', margin:'5px 0', background:'rgba(255,255,255,0.22)', borderRadius:6, fontSize:11 } },
        b.label,
        React.createElement(FC_Handle, { type:'source', position:'right', id:b.id, style: FC_dot })
      ))
    )
  );
}
function FC_ConditionNode(props){
  const data = props.data;
  return React.createElement('div', { style: FC_box('condition',200) },
    React.createElement(FC_Handle, { type:'target', position:'left', style:{ ...FC_dot, background:'#111' } }),
    React.createElement('div', { style: FC_head }, 'CONDITION'),
    data.text ? React.createElement('div', { style:{ marginTop:4 } }, data.text) : null,
    React.createElement('div', { style:{ position:'relative', padding:'5px 8px', margin:'4px 0', background:'rgba(0,0,0,0.08)', borderRadius:6 } },
      'true', React.createElement(FC_Handle, { type:'source', position:'right', id:'true', style:{ ...FC_dot, background:'#16a34a' } })
    ),
    React.createElement('div', { style:{ position:'relative', padding:'5px 8px', margin:'4px 0', background:'rgba(0,0,0,0.08)', borderRadius:6 } },
      'false', React.createElement(FC_Handle, { type:'source', position:'right', id:'false', style:{ ...FC_dot, background:'#dc2626' } })
    )
  );
}
const FC_nodeTypes = { start:FC_StartNode, end:FC_EndNode, buttons:FC_ButtonsNode, condition:FC_ConditionNode, message:FC_SimpleNode, question:FC_SimpleNode, assign:FC_SimpleNode, template:FC_SimpleNode };

function FC_DeletableEdge(props){
  const { id, sourceX, sourceY, targetX, targetY, markerEnd, style, data } = props;
  const dx = Math.abs(targetX - sourceX);
  const c = Math.max(40, dx * 0.5);
  const edgePath = 'M ' + sourceX + ',' + sourceY + ' C ' + (sourceX + c) + ',' + sourceY + ' ' + (targetX - c) + ',' + targetY + ' ' + targetX + ',' + targetY;
  const labelX = (sourceX + targetX) / 2;
  const labelY = (sourceY + targetY) / 2;
  const onDel = function(evt){ evt.stopPropagation(); if (data && data.onDelete) data.onDelete(id); };
  const btn = React.createElement('button', {
    onClick: onDel, title:'Delete connection', className:'nodrag nopan',
    style:{ width:20, height:20, lineHeight:'18px', textAlign:'center', borderRadius:'50%', border:'1px solid #cbd5e1', background:'#fff', color:'#64748b', cursor:'pointer', fontSize:14, fontWeight:700, boxShadow:'0 1px 3px rgba(0,0,0,0.2)' },
    onMouseOver: function(e){ e.currentTarget.style.background='#ef4444'; e.currentTarget.style.color='#fff'; e.currentTarget.style.borderColor='#ef4444'; },
    onMouseOut: function(e){ e.currentTarget.style.background='#fff'; e.currentTarget.style.color='#64748b'; e.currentTarget.style.borderColor='#cbd5e1'; }
  }, '\u00D7');
  const wrap = React.createElement('div', {
    className:'nodrag nopan',
    style:{ position:'absolute', transform:'translate(-50%,-50%) translate(' + labelX + 'px,' + labelY + 'px)', pointerEvents:'all', zIndex:20 }
  }, btn);
  return React.createElement(React.Fragment, null,
    React.createElement(FC_BaseEdge, { id:id, path:edgePath, markerEnd:markerEnd, style:style }),
    React.createElement(FC_EdgeLabelRenderer, null, wrap)
  );
}
const FC_edgeTypes = { deletable: FC_DeletableEdge };

function FC_buildDefinition(nodes, edges){
  const outNodes = nodes.map(function(n){
    const d = Object.assign({}, n.data);
    delete d._type; delete d.onDelete;
    return { id:n.id, type:n.type, position:{ x:Math.round(n.position.x), y:Math.round(n.position.y) }, data:d };
  });
  const outEdges = edges.map(function(e){
    return { id:e.id, source:e.source, target:e.target, sourceHandle: e.sourceHandle != null ? e.sourceHandle : null };
  });
  return { nodes:outNodes, edges:outEdges };
}

function FC_seedDef(flow){
  const def = (flow && flow.definition) ? flow.definition : null;
  if (def && Array.isArray(def.nodes) && def.nodes.length) return { nodes:def.nodes, edges:Array.isArray(def.edges)?def.edges:[] };
  return { nodes:[{ id:'start', type:'start', position:{x:0,y:0}, data:{} }], edges:[] };
}

function FlowCanvas({ flow, templates, API, clientId, onBack, onSaved }){
  const [loaded, setLoaded] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  React.useEffect(function(){
    let alive = true;
    fetch(API + '/flows/' + flow.id + '?clientId=' + clientId)
      .then(function(r){ return r.json(); })
      .then(function(d){ if(!alive) return; if(d && d.id){ setLoaded(d); } else { setLoadErr('Could not load flow'); } })
      .catch(function(e){ if(alive) setLoadErr(e.message); });
    return function(){ alive = false; };
  }, [flow.id]);

  if (loadErr) return React.createElement('div', { style:{ padding:24, color:'#dc2626' } }, 'Error: ' + loadErr);
  if (!loaded) return React.createElement('div', { style:{ padding:24, color:'#64748b' } }, 'Loading flow...');
  return React.createElement(FlowCanvasInner, { flow:loaded, templates:templates, API:API, clientId:clientId, onBack:onBack, onSaved:onSaved });
}

const FC_PALETTE = [
  { type:'message', label:'Message' },
  { type:'buttons', label:'Buttons' },
  { type:'question', label:'Question' },
  { type:'condition', label:'Condition' },
  { type:'assign', label:'Assign' },
  { type:'template', label:'Template' },
  { type:'end', label:'End' }
];
function FC_blankData(type){
  if (type==='buttons') return { text:'Choose an option', buttons:[ { id:'b1', label:'Option 1', reply:'' } ] };
  if (type==='message') return { text:'Your message here' };
  if (type==='question') return { text:'Your question?', variable:'answer' };
  if (type==='condition') return { text:'variable == value' };
  if (type==='assign') return { text:'Assign to agent' };
  if (type==='template') return { templateName:'', language:'en_US' };
  return {};
}

function FC_field(label, el){
  return React.createElement('div', { style:{ marginBottom:12 } },
    React.createElement('div', { style:{ fontSize:11, fontWeight:700, color:'#64748b', marginBottom:4 } }, label),
    el
  );
}
const FC_inp = { width:'100%', boxSizing:'border-box', padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:13, fontFamily:'inherit' };

function FC_Inspector(props){
  const node = props.node;
  if (!node) return null;
  const onChange = props.onChange, onClose = props.onClose;
  const t = node.type;
  const d = node.data || {};
  function set(key, val){ const o = Object.assign({}, d); o[key]=val; onChange(node.id, o); }
  function setBtns(arr){ onChange(node.id, Object.assign({}, d, { buttons:arr })); }

  const rows = [];
  rows.push(React.createElement('div', { key:'hd', style:{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 } },
    React.createElement('div', { style:{ fontSize:13, fontWeight:800, color:'#0f172a', textTransform:'uppercase', letterSpacing:0.5 } }, t==='start' ? 'TRIGGER (START)' : t),
    React.createElement('button', { onClick:onClose, style:{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#94a3b8', lineHeight:1 } }, '\u00D7')
  ));

  if (t==='start'){
    rows.push(React.createElement('div', { key:'si', style:{ fontSize:12, color:'#64748b', marginBottom:12 } }, 'Decide when this flow should start.'));
    rows.push(FC_field('Trigger type', React.createElement('select', { value: props.trigType||'keyword', style:FC_inp, onChange:function(ev){ props.setTrig(ev.target.value, props.trigVal); } },
      React.createElement('option', { value:'keyword' }, 'Keyword'),
      React.createElement('option', { value:'any_message' }, 'Any message'),
      React.createElement('option', { value:'new_contact' }, 'New contact')
    )));
    if ((props.trigType||'keyword')==='keyword'){
      rows.push(FC_field('Keyword (matches if message contains this, case-insensitive)', React.createElement('input', { value: props.trigVal||'', style:FC_inp, placeholder:'e.g. menu', onChange:function(ev){ props.setTrig(props.trigType||'keyword', ev.target.value); } })));
    } else {
      rows.push(React.createElement('div', { key:'nk', style:{ fontSize:11, color:'#94a3b8' } }, props.trigType==='any_message' ? 'Flow starts on ANY incoming message.' : 'Flow starts when a brand-new contact messages for the first time.'));
    }
  }

  if (t==='end') rows.push(React.createElement('div', { key:'e', style:{ fontSize:12, color:'#64748b' } }, 'End node closes the flow.'));

  if (t==='message') rows.push(FC_field('Message text', React.createElement('textarea', { value:d.text||'', rows:4, style:FC_inp, onChange:function(ev){ set('text', ev.target.value); } })));

  if (t==='question'){
    rows.push(FC_field('Question text', React.createElement('textarea', { value:d.text||'', rows:3, style:FC_inp, onChange:function(ev){ set('text', ev.target.value); } })));
    rows.push(FC_field('Save answer to variable', React.createElement('input', { value:d.variable||'', style:FC_inp, placeholder:'e.g. name', onChange:function(ev){ set('variable', ev.target.value); } })));
  }

  if (t==='condition'){
    rows.push(FC_field('Condition expression', React.createElement('input', { value:d.text||'', style:FC_inp, placeholder:'e.g. answer == yes', onChange:function(ev){ set('text', ev.target.value); } })));
    rows.push(React.createElement('div', { key:'ch', style:{ fontSize:11, color:'#94a3b8', marginTop:-4 } }, 'Wire the green (true) and red (false) handles to next nodes.'));
  }

  if (t==='assign') rows.push(FC_field('Assign label / agent', React.createElement('input', { value:d.text||'', style:FC_inp, onChange:function(ev){ set('text', ev.target.value); } })));

  if (t==='template'){ var tpls = props.templates || []; rows.push(FC_field('Approved template', React.createElement('select', { value:d.templateName||'', style:FC_inp, onChange:function(ev){ var nm=ev.target.value; var found=tpls.filter(function(x){return x.name===nm;})[0]; var o=Object.assign({}, d, { templateName:nm, language:(found&&found.language)?found.language:(d.language||'en_US') }); onChange(node.id, o); } }, React.createElement('option', { value:'' }, tpls.length? '-- select a template --':'(no approved templates found)'), tpls.map(function(x){ return React.createElement('option', { key:x.name, value:x.name }, x.name + (x.language?(' ('+x.language+')'):'')); })))); if(d.templateName){ rows.push(React.createElement('div', { key:'tplinfo', style:{ fontSize:11, color:'#94a3b8', marginTop:-4, marginBottom:8 } }, 'Language: ' + (d.language||'en_US'))); var selTpl = tpls.filter(function(x){return x.name===d.templateName;})[0]; var hasImg = selTpl && selTpl.components && selTpl.components.some(function(c){return c.type==='HEADER' && c.format==='IMAGE';}); if(hasImg){ rows.push(FC_field('Header image URL (required for this template)', React.createElement('div', null, React.createElement('input', { value:d.headerImageUrl||'', readOnly:true, placeholder:'upload below or paste URL', style:FC_inp }), React.createElement('input', { type:'file', accept:'image/*', style:{ marginTop:6, width:'100%', fontSize:12 }, onChange:function(ev){ var f=ev.target.files[0]; if(!f) return; var fd=new FormData(); fd.append('image', f); fetch(API + '/upload-image', { method:'POST', body:fd }).then(function(r){return r.json();}).then(function(rd){ if(rd.success){ onChange(node.id, Object.assign({}, d, { headerImageUrl:rd.url })); } }); } }), d.headerImageUrl ? React.createElement('div', { style:{ fontSize:11, color:'#16a34a', marginTop:4 } }, '\u2713 image set') : null))); } } }

  if (t==='buttons'){
    rows.push(FC_field('Body text', React.createElement('textarea', { value:d.text||'', rows:3, style:FC_inp, onChange:function(ev){ set('text', ev.target.value); } })));
    const btns = d.buttons || [];
    rows.push(React.createElement('div', { key:'bl', style:{ fontSize:11, fontWeight:700, color:'#64748b', marginBottom:6 } }, 'BUTTONS (max 3)'));
    btns.forEach(function(b, i){
      rows.push(React.createElement('div', { key:'b'+i, style:{ border:'1px solid #e2e8f0', borderRadius:10, padding:10, marginBottom:8, background:'#f8fafc' } },
        React.createElement('div', { style:{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 } },
          React.createElement('span', { style:{ fontSize:11, fontWeight:700, color:'#475569' } }, 'Button ' + (i+1) + ' (' + b.id + ')'),
          React.createElement('button', { onClick:function(){ const a=btns.slice(); a.splice(i,1); setBtns(a); }, style:{ background:'none', border:'none', color:'#dc2626', cursor:'pointer', fontSize:12, fontWeight:600 } }, 'Remove')
        ),
        React.createElement('input', { value:b.label||'', placeholder:'Button label', style:Object.assign({}, FC_inp, { marginBottom:6 }), onChange:function(ev){ const a=btns.slice(); a[i]=Object.assign({}, b, { label:ev.target.value }); setBtns(a); } }),
        React.createElement('textarea', { value:b.reply||'', rows:2, placeholder:'Reply when clicked (optional)', style:FC_inp, onChange:function(ev){ const a=btns.slice(); a[i]=Object.assign({}, b, { reply:ev.target.value }); setBtns(a); } })
      ));
    });
    if (btns.length < 3) rows.push(React.createElement('button', { key:'addb', onClick:function(){ const a=btns.slice(); const nid='b'+(a.length+1); a.push({ id:nid, label:'Option '+(a.length+1), reply:'' }); setBtns(a); }, style:{ width:'100%', padding:'8px', background:'#fff', border:'1px dashed #cbd5e1', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600, color:'#475569' } }, '+ Add button'));
  }

  return React.createElement('div', { style:{ width:300, flexShrink:0, background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:16, overflowY:'auto' } }, rows);
}

function FlowCanvasInner({ flow, templates, API, clientId, onBack, onSaved }){
  const seed = React.useMemo(function(){ return FC_seedDef(flow); }, [flow.id]);
  const initNodes = React.useMemo(function(){
    const pos = FC_computeLayout(seed);
    return seed.nodes.map(function(n){
      const hasPos = n.position && (n.position.x || n.position.y);
      return { id:n.id, type:n.type, position: hasPos ? n.position : (pos[n.id]||{x:0,y:0}), data: Object.assign({}, n.data, { _type:n.type }) };
    });
  }, [seed]);
  const initEdges = React.useMemo(function(){
    return seed.edges.map(function(e){
      return { id:e.id, source:e.source, target:e.target, sourceHandle: e.sourceHandle || undefined, type:'deletable', markerEnd:'arrowclosed', style:{ stroke:'#94a3b8' } };
    });
  }, [seed]);

  const [nodes, setNodes, onNodesChange] = FC_useNodesState(initNodes);
  const [edges, setEdges, onEdgesChange] = FC_useEdgesState(initEdges);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [selId, setSelId] = useState(null);
  const [trigType, setTrigType] = useState(flow.trigger_type || 'keyword');
  const [trigVal, setTrigVal] = useState(flow.trigger_value || '');
  const idRef = React.useRef(1);

  function setTrig(tt, tv){ setTrigType(tt); setTrigVal(tv); }

  const delEdge = React.useCallback(function(id){ setEdges(function(eds){ return eds.filter(function(e){ return e.id !== id; }); }); }, [setEdges]);
  const edgesWithDelete = edges.map(function(e){ return { ...e, type:'deletable', data:{ ...(e.data||{}), onDelete: delEdge } }; });
  const onConnect = React.useCallback(function(p){ setEdges(function(eds){ return FC_addEdge({ ...p, type:'deletable', markerEnd:'arrowclosed', style:{ stroke:'#0ea5e9', strokeWidth:2 } }, eds); }); }, [setEdges]);

  function onNodeClick(ev, node){ setSelId(node.id); }
  function updateNodeData(id, newData){
    setNodes(function(ns){ return ns.map(function(n){ return n.id===id ? Object.assign({}, n, { data: Object.assign({}, newData, { _type:n.type }) }) : n; }); });
  }
  const selNode = nodes.filter(function(n){ return n.id===selId; })[0] || null;

  function addNode(type){
    const nid = type + '_' + Date.now().toString(36) + '_' + (idRef.current++);
    let maxX = 0; nodes.forEach(function(n){ if(n.position.x > maxX) maxX = n.position.x; });
    const newNode = { id:nid, type:type, position:{ x:maxX + FC_H_GAP, y: 40 + (nodes.length % 5)*FC_V_GAP }, data: Object.assign({}, FC_blankData(type), { _type:type }) };
    setNodes(function(ns){ return ns.concat([newNode]); });
    setSelId(nid);
  }
  function delNode(){
    const sel = nodes.filter(function(n){return n.selected;}).map(function(n){return n.id;});
    setNodes(function(ns){ return ns.filter(function(n){ return !n.selected; }); });
    setEdges(function(es){ return es.filter(function(e){ return sel.indexOf(e.source)===-1 && sel.indexOf(e.target)===-1; }); });
    if (sel.indexOf(selId)!==-1) setSelId(null);
  }

  function save(){
    setSaving(true); setSavedMsg('');
    const def = FC_buildDefinition(nodes, edges);
    fetch(API + '/flows/' + flow.id, {
      method:'PUT', headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify({ clientId:clientId, name:flow.name, trigger_type:trigType, trigger_value:trigVal, definition:def })
    })
      .then(function(r){ return r.json(); })
      .then(function(d){
        setSaving(false);
        if (d && d.id) { setSavedMsg('Saved'); if (onSaved) onSaved(d); setTimeout(function(){ setSavedMsg(''); }, 2000); }
        else setSavedMsg('Error: ' + (d.error || 'unknown'));
      })
      .catch(function(e){ setSaving(false); setSavedMsg('Error: ' + e.message); });
  }

  const palBtn = { display:'block', width:'100%', textAlign:'left', padding:'8px 12px', marginBottom:6, background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:600, color:'#334155' };
  const trigLabel = 'Trigger: ' + trigType + ((trigType==='keyword' && trigVal) ? (' = ' + trigVal) : '');

  return React.createElement('div', { style:{ display:'flex', flexDirection:'column', height:'calc(100vh - 130px)' } },
    React.createElement('div', { style:{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 } },
      React.createElement('div', { style:{ display:'flex', alignItems:'center', gap:12 } },
        React.createElement('button', { onClick:onBack, style:{ padding:'8px 14px', background:'#fff', color:'#334155', border:'1px solid #e2e8f0', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:600 } }, '\u2190 Back'),
        React.createElement('div', { style:{ fontSize:16, fontWeight:700, color:'#0f172a' } }, flow.name),
        React.createElement('div', { style:{ fontSize:12, color:'#64748b' } }, trigLabel)
      ),
      React.createElement('div', { style:{ display:'flex', alignItems:'center', gap:10 } },
        savedMsg ? React.createElement('span', { style:{ fontSize:13, fontWeight:600, color: savedMsg==='Saved' ? '#16a34a' : '#dc2626' } }, savedMsg) : null,
        React.createElement('button', { onClick:save, disabled:saving, style:{ padding:'8px 18px', background: saving ? '#94a3b8' : '#16a34a', color:'#fff', border:'none', borderRadius:8, cursor: saving ? 'default' : 'pointer', fontSize:13, fontWeight:700 } }, saving ? 'Saving...' : 'Save')
      )
    ),
    React.createElement('div', { style:{ flex:1, display:'flex', gap:10, minHeight:0 } },
      React.createElement('div', { style:{ width:170, flexShrink:0, background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:12, padding:12, overflowY:'auto' } },
        React.createElement('div', { style:{ fontSize:11, fontWeight:700, color:'#64748b', letterSpacing:0.5, marginBottom:8 } }, 'ADD NODE'),
        FC_PALETTE.map(function(p){ return React.createElement('button', { key:p.type, onClick:function(){ addNode(p.type); }, style: palBtn }, '+ ' + p.label); }),
        React.createElement('div', { style:{ borderTop:'1px solid #e2e8f0', marginTop:10, paddingTop:10 } },
          React.createElement('button', { onClick:delNode, style:{ ...palBtn, color:'#dc2626', borderColor:'rgba(239,68,68,0.3)' } }, 'Delete selected')
        )
      ),
      React.createElement('div', { style:{ flex:1, background:'#f8fafc', borderRadius:12, border:'1px solid #e2e8f0', overflow:'hidden' } },
        React.createElement(FC_ReactFlow, {
          nodes:nodes, edges:edgesWithDelete,
          onNodesChange:onNodesChange, onEdgesChange:onEdgesChange, onConnect:onConnect,
          onNodeClick:onNodeClick,
          nodeTypes:FC_nodeTypes, edgeTypes:FC_edgeTypes,
          nodesDraggable:true, nodesConnectable:true, elementsSelectable:true,
          fitView:true, fitViewOptions:{ padding:0.2 }
        },
          React.createElement(FC_Background, null),
          React.createElement(FC_Controls, null),
          React.createElement(FC_MiniMap, null)
        )
      ),
      selNode ? React.createElement(FC_Inspector, { node:selNode, templates:templates, onChange:updateNodeData, onClose:function(){ setSelId(null); }, trigType:trigType, trigVal:trigVal, setTrig:setTrig }) : null
    )
  );
}

// ===== END FLOW CANVAS =====


function AIChatPage(props){
  const API = props.API, clientId = props.clientId, P = props.P, s = props.s;
  const [enabled, setEnabled] = React.useState(false);
  const [escalate, setEscalate] = React.useState(true);
  const [prompt, setPrompt] = React.useState('');
  const [faqs, setFaqs] = React.useState([]);
  const [kb, setKb] = React.useState([]);
  const [kbTitle, setKbTitle] = React.useState('');
  const [kbContent, setKbContent] = React.useState('');
  const [nq, setNq] = React.useState('');
  const [na, setNa] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [savedMsg, setSavedMsg] = React.useState('');
  const [loaded, setLoaded] = React.useState(false);

  const loadAll = React.useCallback(async () => {
    try {
      const r1 = await fetch(API + '/ai-settings?clientId=' + clientId);
      const d1 = await r1.json();
      if (d1.success && d1.settings) { setEnabled(!!d1.settings.enabled); setEscalate(d1.settings.escalate_on_unknown !== false); setPrompt(d1.settings.system_prompt || ''); }
      const r2 = await fetch(API + '/ai-faqs?clientId=' + clientId);
      const d2 = await r2.json();
      if (d2.success) setFaqs(d2.faqs || []);
      const r3 = await fetch(API + '/knowledge-base?clientId=' + clientId);
      const d3 = await r3.json();
      if (d3.success) setKb(d3.entries || []);
    } catch(e) {}
    setLoaded(true);
  }, [API, clientId]);

  React.useEffect(() => { loadAll(); }, [loadAll]);

  const saveSettings = async (overrideEnabled) => {
    setSaving(true); setSavedMsg('');
    const en = (overrideEnabled !== undefined) ? overrideEnabled : enabled;
    try {
      const r = await fetch(API + '/ai-settings', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ clientId, enabled: en, system_prompt: prompt, escalate_on_unknown: escalate }) });
      const d = await r.json();
      if (d.success) { setSavedMsg('Saved'); setTimeout(()=>setSavedMsg(''), 2000); }
    } catch(e) {}
    setSaving(false);
  };

  const addFaq = async () => {
    if (!nq.trim() || !na.trim()) return;
    try {
      const r = await fetch(API + '/ai-faqs', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ clientId, question: nq, answer: na }) });
      const d = await r.json();
      if (d.success && d.faq) { setFaqs(prev => [...prev, d.faq]); setNq(''); setNa(''); }
    } catch(e) {}
  };

  const delFaq = async (id) => {
    try {
      await fetch(API + '/ai-faqs/' + id, { method:'DELETE', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ clientId }) });
      setFaqs(prev => prev.filter(f => f.id !== id));
    } catch(e) {}
  };

  const addKB = async () => {
    if (!kbTitle.trim() || !kbContent.trim()) return;
    try {
      const r = await fetch(API + '/knowledge-base', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ clientId, title: kbTitle, content: kbContent }) });
      const d = await r.json();
      if (d.success && d.entry) { setKb(prev => [...prev, d.entry]); setKbTitle(''); setKbContent(''); }
    } catch(e) {}
  };

  const delKB = async (id) => {
    try {
      await fetch(API + '/knowledge-base/' + id, { method:'DELETE', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ clientId }) });
      setKb(prev => prev.filter(k => k.id !== id));
    } catch(e) {}
  };

  const toggleKB = async (id, cur) => {
    try {
      const r = await fetch(API + '/knowledge-base/' + id + '/status', { method:'PUT', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ clientId, status: !cur }) });
      const d = await r.json();
      if (d.success && d.entry) { setKb(prev => prev.map(k => k.id===id ? d.entry : k)); }
    } catch(e) {}
  };

  const toggleEnabled = () => { const ne = !enabled; setEnabled(ne); saveSettings(ne); };

  if (!loaded) return React.createElement('div', null, React.createElement('div',{style:s.pageTitle},'AI Assistant'), React.createElement('div',{style:s.card}, React.createElement('div',{style:s.emptyBox},'Loading...')));

  return React.createElement('div', null,
    React.createElement('div',{style:s.pageTitle},'AI Assistant'),
    React.createElement('div',{style:{...s.card, marginBottom:16}},
      React.createElement('div',{style:{display:'flex',justifyContent:'space-between',alignItems:'center'}},
        React.createElement('div',null,
          React.createElement('div',{style:{fontSize:16,fontWeight:700,color:'#0f172a'}},'AI Chat Agent'),
          React.createElement('div',{style:{fontSize:13,color:'#64748b',marginTop:4}},'When ON, the AI answers customer questions automatically using your FAQ. If it cannot answer, it escalates to a human agent.')
        ),
        React.createElement('div',{onClick:toggleEnabled,style:{cursor:'pointer',width:52,height:28,borderRadius:14,background:enabled?P:'#cbd5e1',position:'relative',transition:'background .2s',flexShrink:0,marginLeft:16}},
          React.createElement('div',{style:{position:'absolute',top:3,left:enabled?27:3,width:22,height:22,borderRadius:11,background:'#fff',transition:'left .2s'}})
        )
      ),
      React.createElement('div',{style:{marginTop:14,display:'flex',alignItems:'center',gap:8}},
        React.createElement('input',{type:'checkbox',checked:escalate,onChange:e=>{setEscalate(e.target.checked);}, id:'esc'}),
        React.createElement('label',{htmlFor:'esc',style:{fontSize:13,color:'#374151'}},'Escalate to a human agent when the AI cannot answer'),
        React.createElement('button',{onClick:()=>saveSettings(),disabled:saving,style:{...s.btn(),marginLeft:'auto',padding:'6px 16px',fontSize:13}}, saving?'Saving...':'Save'),
        savedMsg?React.createElement('span',{style:{color:'#16a34a',fontSize:13,fontWeight:600}},savedMsg):null
      )
    ),
    React.createElement('div',{style:{...s.card}},
      React.createElement('div',{style:{fontSize:15,fontWeight:700,color:'#0f172a',marginBottom:4}},'Knowledge / FAQ'),
      React.createElement('div',{style:{fontSize:13,color:'#64748b',marginBottom:16}},'Add the questions your customers ask and the answers the AI should give. The AI only answers from this list.'),
      React.createElement('div',{style:{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap'}},
        React.createElement('input',{value:nq,onChange:e=>setNq(e.target.value),placeholder:'Question (e.g. What are your hours?)',style:{...s.input,flex:'1 1 240px',margin:0}}),
        React.createElement('input',{value:na,onChange:e=>setNa(e.target.value),placeholder:'Answer',style:{...s.input,flex:'1 1 240px',margin:0}}),
        React.createElement('button',{onClick:addFaq,style:{...s.btn(),padding:'10px 18px'}},'+ Add')
      ),
      faqs.length===0 ? React.createElement('div',{style:s.emptyBox},'No FAQ yet. Add your first question above.') :
      React.createElement('div',null, faqs.map(f =>
        React.createElement('div',{key:f.id,style:{border:'1px solid #e5e7eb',borderRadius:10,padding:'12px 14px',marginBottom:10,display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12}},
          React.createElement('div',null,
            React.createElement('div',{style:{fontSize:14,fontWeight:600,color:'#0f172a'}},'Q: '+f.question),
            React.createElement('div',{style:{fontSize:13,color:'#475569',marginTop:4}},'A: '+f.answer)
          ),
          React.createElement('button',{onClick:()=>delFaq(f.id),style:{background:'#fef2f2',color:'#dc2626',border:'none',borderRadius:8,padding:'6px 12px',fontSize:13,fontWeight:600,cursor:'pointer',flexShrink:0}},'Delete')
        )
      ))
    ),
    React.createElement('div',{style:{...s.card,marginTop:16}},
      React.createElement('div',{style:{fontSize:15,fontWeight:700,color:'#0f172a',marginBottom:4}},'Knowledge Base'),
      React.createElement('div',{style:{fontSize:13,color:'#64748b',marginBottom:16}},'Add documents, policies, or any info the AI can use to answer. Longer free-text than FAQ. The AI reads active entries alongside your FAQ.'),
      React.createElement('div',{style:{display:'flex',flexDirection:'column',gap:8,marginBottom:16}},
        React.createElement('input',{value:kbTitle,onChange:e=>setKbTitle(e.target.value),placeholder:'Title (e.g. Refund Policy)',style:{...s.input,margin:0}}),
        React.createElement('textarea',{value:kbContent,onChange:e=>setKbContent(e.target.value),placeholder:'Content (paste any info the AI should know)',rows:4,style:{...s.input,margin:0,resize:'vertical',fontFamily:'inherit'}}),
        React.createElement('button',{onClick:addKB,style:{...s.btn(),padding:'10px 18px',alignSelf:'flex-start'}},'+ Add Entry')
      ),
      kb.length===0 ? React.createElement('div',{style:s.emptyBox},'No knowledge base entries yet.') :
      React.createElement('div',null, kb.map(k =>
        React.createElement('div',{key:k.id,style:{border:'1px solid #e5e7eb',borderRadius:10,padding:'12px 14px',marginBottom:10,display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12,opacity:k.status?1:0.5}},
          React.createElement('div',{style:{flex:1}},
            React.createElement('div',{style:{fontSize:14,fontWeight:600,color:'#0f172a'}},k.title),
            React.createElement('div',{style:{fontSize:13,color:'#475569',marginTop:4,whiteSpace:'pre-wrap'}},k.content)
          ),
          React.createElement('div',{style:{display:'flex',flexDirection:'column',gap:6,flexShrink:0}},
            React.createElement('button',{onClick:()=>toggleKB(k.id,k.status),style:{background:k.status?'#ecfdf5':'#f1f5f9',color:k.status?'#059669':'#64748b',border:'none',borderRadius:8,padding:'6px 12px',fontSize:12,fontWeight:600,cursor:'pointer'}},k.status?'Active':'Off'),
            React.createElement('button',{onClick:()=>delKB(k.id),style:{background:'#fef2f2',color:'#dc2626',border:'none',borderRadius:8,padding:'6px 12px',fontSize:12,fontWeight:600,cursor:'pointer'}},'Delete')
          )
        )
      ))
    )
  );
}

export default function App() {
  const [user, setUser] = useState(() => { try { const s = localStorage.getItem('wasavo_user'); return s ? JSON.parse(s) : null; } catch(e) { return null; } });
  const [page, setPage] = useState('dashboard');
  const [waOpen, setWaOpen] = useState(true);
  const [campOpen, setCampOpen] = useState(false);
  const [settingOpen, setSettingOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  const [flows, setFlows] = useState([]); const [selectedFlow, setSelectedFlow] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [statusOk, setStatusOk] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactLists, setContactLists] = useState([]);
  const [segments, setSegments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [autoReplies, setAutoReplies] = useState([]);
  const [newAutoReply, setNewAutoReply] = useState({ keyword: '', reply: '' });
  const [newCampaign, setNewCampaign] = useState({ name: '', message: '', listId: '' });
  const [newContact, setNewContact] = useState({ name: '', phone: '', country: '' });
  const [team, setTeam] = useState([]);
  const loadTeam = () => fetch(API+'/team-members?clientId='+user.clientId).then(r=>r.json()).then(d=>{ if(d.success) setTeam(d.members); }).catch(()=>{});
  useEffect(()=>{ if(user&&user.clientId) loadTeam(); }, []);
  const [assignments, setAssignments] = useState([]);
  const loadAssignments = () => fetch(API+'/assignments?clientId='+user.clientId).then(r=>r.json()).then(d=>{ if(d.success) setAssignments(d.assignments); }).catch(()=>{});
  useEffect(()=>{ if(user&&user.clientId) loadAssignments(); }, []);
  const [newMember, setNewMember] = useState({ name: '', email: '', role: 'Agent', password: '' });
  const [showAddMember, setShowAddMember] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [contactPage, setContactPage] = useState(1);
  const [search, setSearch] = useState('');
  const [importPreview, setImportPreview] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showBulkMenu, setShowBulkMenu] = useState(false); const [selectedConvo, setSelectedConvo] = useState(null); const [selectedCampaign, setSelectedCampaign] = useState(null); const [replyMsg, setReplyMsg] = useState("");
  const [usage, setUsage] = useState(null);
  const [dash, setDash] = useState(null);

  const totalSent = campaigns.reduce((a,c)=>a+(c.sent||0),0);
  const totalRead = campaigns.reduce((a,c)=>a+(c.read||0),0);
  const readRate = totalSent>0?Math.round(totalRead/totalSent*100):0;
  const unreadCount = inbox.filter(m=>m.direction==='inbound'&&!m.is_read).length;
const prevInboxLen = React.useRef(0);
React.useEffect(()=>{
  const interval = setInterval(async ()=>{
    if(!user) return;
    const r = await fetch(`${API}/messages?clientId=${user.clientId}`);
    const d = await r.json();
    if(d.success){
      if(d.messages.length > prevInboxLen.current){
        try{ new Audio("https://www.soundjay.com/buttons/beep-01a.mp3").play(); }catch(e){}
      }
      prevInboxLen.current = d.messages.length;
      setInbox(d.messages);
    }
  },10000);
  return ()=>clearInterval(interval);
},[user]);
  useEffect(()=>{
    if(!user) return;
    fetch(`${API}/contacts?clientId=${user.clientId}`).then(r=>r.json()).then(d=>{ if(d.success) setContacts(d.contacts); });
    fetch(`${API}/contact-lists?clientId=${user.clientId}`).then(r=>r.json()).then(d=>{ if(d.success) setContactLists(d.lists); });
    fetch(`${API}/segments?clientId=${user.clientId}`).then(r=>r.json()).then(d=>{ if(d.success) setSegments(d.segments); }); fetch(`${API}/campaigns?clientId=${user.clientId}`).then(r=>r.json()).then(d=>{ if(d.success) setCampaigns(d.campaigns); }); fetch(`${API}/messages?clientId=${user.clientId}`).then(r=>r.json()).then(d=>{ if(d.success) setInbox(d.messages); }); fetch(`${API}/usage?clientId=${user.clientId}`).then(r=>r.json()).then(d=>{ if(d.success) setUsage(d.usage); }); fetch(`${API}/dashboard?clientId=${user.clientId}`).then(r=>r.json()).then(d=>{ if(d.success) setDash(d.data); }); fetch(`${API}/auto-replies?clientId=${user.clientId}`).then(r=>r.json()).then(d=>{ if(d.success) setAutoReplies(d.autoReplies); });
    fetch(`${API}/flows?clientId=${user.clientId}`).then(r=>r.json()).then(d=>{ if(Array.isArray(d)) setFlows(d); }).catch(()=>{});
  },[user]);

  const handleLogin = (userData) => { try { localStorage.setItem('wasavo_user', JSON.stringify(userData)); } catch(e) {} setUser(userData); setPage('dashboard'); };
  const handleLogout = () => { try { localStorage.removeItem('wasavo_user'); localStorage.removeItem('wasavo_token'); } catch(e) {} setUser(null); setPage('dashboard'); setContacts([]); setCampaigns([]); setTemplates([]); };

  const sendMessage = async () => {
    if(!phone||!message){setStatus('Please enter phone and message.');setStatusOk(false);return;}
    setLoading(true);
    try {
      const res = await fetch(`${API}/send-message`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({phone,message,clientId:user.clientId})});
      const data = await res.json();
      if(data.success){setStatus('Message sent!');setStatusOk(true);setPhone('');setMessage('');}
      else{setStatus('Failed: '+(data.error||'Unknown'));setStatusOk(false);}
    } catch(e){setStatus('Cannot connect to server.');setStatusOk(false);}
    setLoading(false);
  };

  const sendBulkCampaign = async () => {
    if(!newCampaign.name){setStatus('Campaign name required.');setStatusOk(false);return;}
    if(!newCampaign.templateName){setStatus('Please select a template.');setStatusOk(false);return;}
    if(!newCampaign.listId&&!newCampaign.segmentId){setStatus('Please select a contact list or segment.');setStatusOk(false);return;}
    const selTemplate=templates.find(t=>t.name===newCampaign.templateName);
    const needsImage=selTemplate&&selTemplate.components&&selTemplate.components.some(c=>c.type==='HEADER'&&c.format==='IMAGE');
    if(needsImage&&!newCampaign.headerImageUrl){setStatus('This template requires a header image.');setStatusOk(false);return;}
    let targetContacts=[];
    if(newCampaign.listId) targetContacts=contacts.filter(c=>c.list_id==newCampaign.listId);
    else if(newCampaign.segmentId) targetContacts=contacts.filter(c=>c.segment_id==newCampaign.segmentId);
    if(targetContacts.length===0){setStatus('No contacts in selected list/segment.');setStatusOk(false);return;}
    setLoading(true);
    setStatus('Sending to '+targetContacts.length+' contacts...');
    let sent=0;
    const sentContacts=[];
    const _skip=(newCampaign.scheduleType==='scheduled'&&newCampaign.scheduledAt);
    for(const c of (_skip?[]:targetContacts)){
      try{
        const res=await fetch(`${API}/send-template`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({phone:c.phone,templateName:newCampaign.templateName,languageCode:templates.find(t=>t.name===newCampaign.templateName)?.language||'en_US',clientId:user.clientId,headerImageUrl:newCampaign.headerImageUrl||null})});
        const d=await res.json();
        if(d.success){sent++;sentContacts.push({phone:c.phone,name:c.name,success:true,waMessageId:d.data?.messages?.[0]?.id});}
        else{sentContacts.push({phone:c.phone,name:c.name,success:false});}
      }catch(e){sentContacts.push({phone:c.phone,name:c.name,success:false});}
    }
    const isScheduled=newCampaign.scheduleType==='scheduled'&&newCampaign.scheduledAt;
    const campRes=await fetch(`${API}/campaigns`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({clientId:user.clientId,name:newCampaign.name,templateName:newCampaign.templateName,listId:newCampaign.listId||null,segmentId:newCampaign.segmentId||null,sent:isScheduled?0:sent,status:isScheduled?'Scheduled':'Completed',scheduledAt:newCampaign.scheduledAt?new Date(newCampaign.scheduledAt).toISOString():null,scheduleStatus:isScheduled?'scheduled':'immediate',headerImageUrl:newCampaign.headerImageUrl||null,languageCode:templates.find(t=>t.name===newCampaign.templateName)?.language||'en_US'})});
    const campData=await campRes.json();
    if(campData.success){
      const campId = campData.campaign?.id;
      if(campId && isScheduled){for(const c of targetContacts){await fetch(API+'/campaign-message',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({campaignId:campId,clientId:user.clientId,phone:c.phone,name:c.name,status:'scheduled',waMessageId:null})});}} else if(campId && sentContacts && sentContacts.length>0){
        for(const sc of sentContacts){
          await fetch(`${API}/campaign-message`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({campaignId:campId,clientId:user.clientId,phone:sc.phone,name:sc.name,status:sc.success?'sent':'failed',waMessageId:sc.waMessageId||null})});
        }
      }
      fetch(`${API}/campaigns?clientId=${user.clientId}`).then(r=>r.json()).then(d=>{if(d.success)setCampaigns(d.campaigns);});
    }
    if(sent===0){setStatus('Campaign failed! Messages could not be delivered. Check template or contacts.');setStatusOk(false);}else if(sent<targetContacts.length){setStatus('Partially sent: '+sent+' of '+targetContacts.length+' contacts delivered.');setStatusOk(true);}else{setStatus('Campaign sent successfully to '+sent+' contacts!');setStatusOk(true);}
    setNewCampaign({name:'',message:'',listId:'',segmentId:'',templateName:'',headerImageUrl:''});setLoading(false);setPage('campaigns');
  };

  const loadTemplates = async () => {
    try{
      const res = await fetch(`${API}/get-templates?clientId=${user.clientId}`);
      const data = await res.json();
      if(data.success) setTemplates(data.templates);
      else setTemplates([]);
    }catch(e){setTemplates([]);}
  };

  const handleXLSX = (e) => {
    const file = e.target.files[0]; if(!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const wb = XLSX.read(ev.target.result,{type:'binary'});
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws,{header:1});
      const parsed = data.slice(1).map((row,i)=>({
        id:i+1,
        name:(row[0]||'').toString().trim()||'Unknown',
        phone:(row[1]||'').toString().trim(),
        country:(row[2]||'Saudi Arabia').toString().trim(),
        status:true,listId:'',segmentId:''
      })).filter(c=>c.phone);
      setImportPreview(parsed);
    };
    reader.readAsBinaryString(file);
  };

  const handleCSVFile = (e) => {
    const file = e.target.files[0]; if(!file) return;
    if(file.name.endsWith('.xlsx')||file.name.endsWith('.xls')){
      handleXLSX(e); return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = ev.target.result.split('\n').filter(l=>l.trim());
      const parsed = lines.slice(1).map((line,i)=>{
        const parts = line.split(',');
        return{id:i+1,name:(parts[0]||'Unknown').trim(),phone:(parts[1]||'').trim(),country:(parts[2]||'Saudi Arabia').trim(),status:true,listId:'',segmentId:''};
      }).filter(c=>c.phone);
      setImportPreview(parsed);
    };
    reader.readAsText(file);
  };

  const doImport = async () => {
    const batchSize=200;
    const batches=[];
    for(let i=0;i<importPreview.length;i+=batchSize){ batches.push(importPreview.slice(i,i+batchSize)); }
    let total=0;
    for(const batch of batches){
      try{
        const res = await fetch(`${API}/contacts`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({clientId:user.clientId,contacts:batch})});
        const d = await res.json();
        if(d.success) total+=d.inserted;
      }catch(e){}
    }
    if(importPreview[0]?.listId){
      const listTitle = contactLists.find(l=>l.id==importPreview[0].listId)?.title||'Imported List';
      await fetch(`${API}/contact-lists`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({clientId:user.clientId,title:listTitle})});
    } else {
      await fetch(`${API}/contact-lists`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({clientId:user.clientId,title:'Imported List '+(contactLists.length+1)})});
    }
    const r = await fetch(`${API}/contacts?clientId=${user.clientId}`);
    const d = await r.json();
    if(d.success) setContacts(d.contacts);
    const lr = await fetch(`${API}/contact-lists?clientId=${user.clientId}`);
    const ld = await lr.json();
    if(ld.success) setContactLists(ld.lists);
    setImportPreview([]);
    setStatus(total+' contacts imported!');setStatusOk(true);
    setPage('contacts');
  };

  if(!user) return <LoginPage onLogin={handleLogin}/>;

  const s = {
    app:{display:'flex',minHeight:'100vh',background:'#f5f7f9',fontFamily:"'Segoe UI',sans-serif",fontSize:'14px'},
    sidebar:{width:sidebarOpen?'260px':'0',background:'linear-gradient(180deg,#0b2a1a 0%,#0e3826 100%)',color:'#fff',transition:'width 0.3s',overflow:'hidden',flexShrink:0,display:'flex',flexDirection:'column',position:'fixed',height:'100vh',zIndex:100},
    logoBox:{padding:'20px',borderBottom:'1px solid rgba(255,255,255,0.08)',display:'flex',alignItems:'center',gap:'10px'},
    logoCircle:{width:'38px',height:'38px',background:'#16a34a',borderRadius:'10px',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:'700',fontSize:'15px',flexShrink:0},
    nav:{flex:1,padding:'10px 0',overflowY:'auto'},
    navItem:(a)=>({padding:'11px 14px',cursor:'pointer',background:a?'#16a34a':'transparent',color:a?'#fff':'#9fbcab',display:'flex',alignItems:'center',gap:'12px',fontSize:'13px',fontWeight:a?'600':'500',borderRadius:'10px',margin:'2px 14px',transition:'all 0.15s',boxShadow:a?'0 6px 16px rgba(22,163,74,0.35)':'none'}),
    subItem:(a)=>({padding:'9px 14px 9px 42px',cursor:'pointer',color:a?'#fff':'#9fbcab',fontSize:'13px',fontWeight:a?'600':'400',background:a?'rgba(22,163,74,0.85)':'transparent',borderRadius:'10px',margin:'1px 14px'}),
    menuHeader:(a)=>({padding:'11px 14px',cursor:'pointer',color:a?'#fff':'#6f8d7c',display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:'11px',fontWeight:'600',textTransform:'uppercase',letterSpacing:'0.05em',margin:'8px 14px 0'}),
    main:{marginLeft:sidebarOpen?'260px':'0',flex:1,display:'flex',flexDirection:'column',minHeight:'100vh',transition:'margin 0.3s'},
    topbar:{background:'#fff',padding:'12px 24px',display:'flex',justifyContent:'space-between',alignItems:'center',borderBottom:'1px solid #ecedf2',position:'sticky',top:0,zIndex:50},
    content:{flex:1,padding:'24px'},
    pageTitle:{fontSize:'20px',fontWeight:'700',color:'#0f172a',marginBottom:'20px'},
    kpiGrid:{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'16px',marginBottom:'24px'},
    kpiCard:{background:'#fff',borderRadius:'12px',padding:'18px 20px',display:'flex',alignItems:'center',gap:'14px',border:'1px solid #ecedf2'},
    kpiIcon:(bg)=>({width:'42px',height:'42px',background:'#ecfdf3',borderRadius:'10px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'18px',flexShrink:0,color:P}),
    kpiNum:{fontSize:'22px',fontWeight:'700',color:'#0f172a'},
    kpiLabel:{fontSize:'12px',color:'#64748b',marginTop:'2px'},
    card:{background:'#fff',borderRadius:'12px',padding:'20px',border:'1px solid #ecedf2',marginBottom:'20px'},
    cardTitle:{fontSize:'15px',fontWeight:'600',color:'#0f172a',marginBottom:'16px'},
    label:{fontSize:'12px',fontWeight:'600',color:'#64748b',marginBottom:'5px',display:'block',textTransform:'uppercase',letterSpacing:'0.5px'},
    input:{width:'100%',padding:'10px 14px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',marginBottom:'12px',boxSizing:'border-box',color:'#0f172a',background:'#fff'},
    textarea:{width:'100%',padding:'10px 14px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',marginBottom:'12px',boxSizing:'border-box',height:'100px',resize:'vertical',color:'#0f172a'},
    btn:(bg)=>({background:bg||P,color:'#fff',border:'none',padding:'10px 20px',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',display:'inline-flex',alignItems:'center',gap:'6px'}),
    btnOut:{background:'#fff',color:'#0f172a',border:'1px solid #e2e8f0',padding:'8px 16px',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600'},
    statusMsg:(ok)=>({padding:'10px 14px',borderRadius:'8px',background:ok?'#ecfdf3':'#fef2f2',color:ok?'#15803d':'#991b1b',marginTop:'10px',fontSize:'13px'}),
    table:{width:'100%',borderCollapse:'collapse',fontSize:'13px'},
    th:{background:'#f8fafc',padding:'11px 14px',textAlign:'left',fontWeight:'600',color:'#64748b',fontSize:'12px',borderBottom:'1px solid #ecedf2'},
    td:{padding:'12px 14px',borderBottom:'1px solid #f1f5f9',color:'#334155',verticalAlign:'middle'},
    badge:(c)=>({background:c+'22',color:c,padding:'3px 10px',borderRadius:'20px',fontSize:'11px',fontWeight:'600',display:'inline-block'}),
    toggle:(on)=>({width:'40px',height:'22px',background:on?P:'#d1d5db',borderRadius:'11px',cursor:'pointer',position:'relative',transition:'background 0.2s',flexShrink:0}),
    toggleDot:(on)=>({position:'absolute',top:'3px',left:on?'21px':'3px',width:'16px',height:'16px',background:'#fff',borderRadius:'50%',transition:'left 0.2s'}),
    uploadBox:{border:'2px dashed #e2e8f0',borderRadius:'10px',padding:'28px',textAlign:'center',marginBottom:'16px',background:'#f8fafc'},
    emptyBox:{textAlign:'center',color:'#94a3b8',padding:'48px 20px'},
    searchInput:{padding:'8px 14px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',width:'220px',color:'#334155'},
    select:{padding:'7px 10px',border:'1px solid #e2e8f0',borderRadius:'6px',fontSize:'13px',color:'#334155',background:'#fff'},
    row:{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px',flexWrap:'wrap',gap:'10px'},
    actionBtn:(c)=>({background:'none',border:'none',color:c||'#64748b',cursor:'pointer',fontSize:'15px',padding:'4px'}),
  };

  const Toggle = ({on,onToggle}) => (
    <div style={s.toggle(on)} onClick={onToggle}><div style={s.toggleDot(on)}/></div>
  );

  const filteredContacts = contacts.filter(c=>c.name?.toLowerCase().includes(search.toLowerCase())||c.phone?.includes(search));
  const totalPages = Math.ceil(filteredContacts.length/perPage);
  const pagedContacts = filteredContacts.slice((contactPage-1)*perPage, contactPage*perPage);

  return (
    <div style={s.app}>
      <div style={s.sidebar}>
        <div style={s.logoBox}>
          <img src={process.env.PUBLIC_URL + '/icon-192.png'} alt='Wasavo' style={{width:'40px',height:'40px',objectFit:'contain'}}/>
          <div><span style={{color:'#fff',fontWeight:'700',fontSize:'16px'}}>Wasavo</span></div>
        </div>
        <div style={{padding:'12px 14px',borderBottom:'1px solid rgba(255,255,255,0.08)'}}>
          <div style={{background:'rgba(255,255,255,0.06)',borderRadius:'10px',padding:'11px 13px',display:'flex',alignItems:'center',gap:'10px'}}>
            <div style={{width:'34px',height:'34px',borderRadius:'50%',background:'#16a34a',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:'700',fontSize:'13px',flexShrink:0}}>{user.name.charAt(0)}</div>
            <div style={{minWidth:0}}>
              <div style={{fontSize:'10px',color:'#7fa18e'}}>Logged in as</div>
              <div style={{fontSize:'13px',color:'#fff',fontWeight:'600',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{user.clientName}</div>
            </div>
          </div>
        </div>
        <div style={s.nav}>
          <div style={s.navItem(page==='dashboard')} onClick={()=>setPage('dashboard')}>Dashboard</div>
          <div style={s.menuHeader(waOpen)} onClick={()=>setWaOpen(!waOpen)}>
            <span>WhatsApp</span><span style={{fontSize:'10px'}}>{waOpen?'v':'^'}</span>
          </div>
          {waOpen && <>
            <div style={s.subItem(page==='wa-overview')} onClick={()=>setPage('wa-overview')}>Overview</div>
            <div style={s.subItem(page==='contact-lists')} onClick={()=>setPage('contact-lists')}>Contact Lists</div>
            <div style={s.subItem(page==='segments')} onClick={()=>setPage('segments')}>Segments</div>
            <div style={s.subItem(page==='contacts')} onClick={()=>setPage('contacts')}>Contacts</div>
            <div style={s.subItem(page==='auto-replies')} onClick={()=>setPage('auto-replies')}>Auto Replies</div>
            <div style={s.subItem(page==='templates')} onClick={()=>setPage('templates')}>Templates</div>
          </>}
          <div style={s.menuHeader(campOpen)} onClick={()=>setCampOpen(!campOpen)}>
            <span>Campaigns</span><span style={{fontSize:'10px'}}>{campOpen?'v':'^'}</span>
          </div>
          {campOpen && <>
            <div style={s.subItem(page==='campaigns')} onClick={()=>setPage('campaigns')}>All Campaigns</div>
            <div style={s.subItem(page==='new-campaign')} onClick={()=>setPage('new-campaign')}>New Campaign</div>
          </>}
          <div style={s.menuHeader(autoOpen)} onClick={()=>setAutoOpen(!autoOpen)}>
            <span>Automation</span><span style={{fontSize:'10px'}}>{autoOpen?'v':'^'}</span>
          </div>
          {autoOpen && <>
            <div style={s.subItem(page==='flows')} onClick={()=>setPage('flows')}>Flows</div>
          </>}
          <div style={s.navItem(page==='inbox')} onClick={()=>setPage('inbox')}>
            Inbox {unreadCount>0&&<span style={{background:P,color:'#fff',borderRadius:'10px',padding:'1px 7px',fontSize:'11px',marginLeft:'auto'}}>{unreadCount}</span>}
          </div>
          <div style={s.navItem(page==='team')} onClick={()=>setPage('team')}>Team Member</div>
          <div style={s.navItem(page==='ticket')} onClick={()=>setPage('ticket')}>My Ticket</div>
          <div style={s.menuHeader(aiOpen)} onClick={()=>setAiOpen(!aiOpen)}>
            <span>AI Assistant</span><span style={{fontSize:'10px'}}>{aiOpen?'v':'^'}</span>
          </div>
          {aiOpen&&<div style={s.subItem(page==='ai-chat')} onClick={()=>setPage('ai-chat')}>AI Chat</div>}
          <div style={s.menuHeader(settingOpen)} onClick={()=>setSettingOpen(!settingOpen)}>
            <span>Setting</span><span style={{fontSize:'10px'}}>{settingOpen?'v':'^'}</span>
          </div>
          {settingOpen&&<>
            <div style={s.subItem(page==='settings-profile')} onClick={()=>setPage('settings-profile')}>Profile</div>
            <div style={s.subItem(page==='settings-billing')} onClick={()=>setPage('settings-billing')}>Billing</div>
            <div style={s.subItem(page==='settings-api')} onClick={()=>setPage('settings-api')}>API Keys</div>
          </>}
          <div style={s.navItem(page==='api-docs')} onClick={()=>setPage('api-docs')}>API</div>
        </div>
        <div style={{padding:'14px',borderTop:'1px solid rgba(255,255,255,0.08)'}}>
          <button onClick={handleLogout} style={{width:'100%',padding:'11px',background:'rgba(239,68,68,0.12)',color:'#fca5a5',border:'1px solid rgba(239,68,68,0.25)',borderRadius:'10px',cursor:'pointer',fontSize:'13px',fontWeight:'600',display:'flex',alignItems:'center',justifyContent:'center',gap:'8px'}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="m16 17 5-5-5-5"/><path d="M21 12H9"/></svg>Logout</button>
        </div>
      </div>

      <div style={s.main}>
        <div style={s.topbar}>
          <div style={{display:'flex',alignItems:'center',gap:'16px',flex:1}}>
            <button onClick={()=>setSidebarOpen(!sidebarOpen)} style={{background:'none',border:'none',cursor:'pointer',color:'#64748b',display:'flex',padding:'4px'}}><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h18"/></svg></button>
            <div style={{position:'relative',maxWidth:'380px',width:'100%'}}>
              <span style={{position:'absolute',left:'14px',top:'50%',transform:'translateY(-50%)',color:'#94a3b8',display:'flex'}}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg></span>
              <input placeholder="Search anything..." style={{width:'100%',padding:'9px 14px 9px 40px',border:'1px solid #e8ecf0',borderRadius:'24px',fontSize:'13px',boxSizing:'border-box',background:'#f6f8fa',color:'#334155',outline:'none'}}/>
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:'18px'}}>
            <div onClick={()=>setPage('inbox')} style={{position:'relative',cursor:'pointer',color:'#64748b',display:'flex'}}><svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>{unreadCount>0&&<span style={{position:'absolute',top:'-6px',right:'-6px',background:'#16a34a',color:'#fff',fontSize:'10px',fontWeight:'700',borderRadius:'10px',minWidth:'17px',height:'17px',display:'flex',alignItems:'center',justifyContent:'center',padding:'0 4px'}}>{unreadCount}</span>}</div>
            <div style={{position:'relative'}}><div style={{display:'flex',alignItems:'center',gap:'10px',cursor:'pointer'}} onClick={()=>setShowProfileMenu(!showProfileMenu)}><div style={{width:'34px',height:'34px',background:P,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:'700',fontSize:'13px'}}>{user.name.charAt(0)}</div>
            <span style={{fontSize:'13px',fontWeight:'600',color:'#0f172a'}}>{user.name}</span></div>{showProfileMenu&&(<div onClick={()=>setShowProfileMenu(false)} style={{position:'fixed',top:0,left:0,right:0,bottom:0,zIndex:1999}}/>)}{showProfileMenu&&(<div style={{position:'absolute',top:'48px',right:0,background:'#fff',borderRadius:'12px',boxShadow:'0 10px 40px rgba(0,0,0,0.18)',border:'1px solid #e5e7eb',width:'250px',zIndex:2000,overflow:'hidden'}}><div style={{padding:'16px'}}><div style={{fontSize:'14px',fontWeight:'700',color:'#1e2139'}}>{user.name}</div><div style={{fontSize:'12px',color:'#6b7280',marginTop:'3px'}}>{user.email}</div><div style={{display:'inline-block',marginTop:'10px',fontSize:'11px',fontWeight:'600',color:P,background:'#f0fdf4',padding:'3px 10px',borderRadius:'20px'}}>{user.clientName}</div></div><div onClick={()=>{setShowProfileMenu(false);setPage('settings-profile');}} style={{padding:'12px 16px',fontSize:'13px',color:'#374151',borderTop:'1px solid #f3f4f6',cursor:'pointer'}}>Profile Settings</div><div onClick={()=>{setShowProfileMenu(false);handleLogout();}} style={{padding:'12px 16px',fontSize:'13px',color:'#dc2626',fontWeight:'600',borderTop:'1px solid #f3f4f6',cursor:'pointer'}}>Logout</div></div>)}</div>
          </div>
        </div>

        <div style={s.content}>

          {page==='dashboard'&&(
            <div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:'12px',marginBottom:'22px'}}>
                <div>
                  <div style={{fontSize:'24px',fontWeight:'800',color:'#0f172a'}}>{(()=>{const h=new Date().getHours();return h<12?'Good morning':h<17?'Good afternoon':'Good evening';})()}, {user.clientName}!</div>
                  <div style={{fontSize:'14px',color:'#64748b',marginTop:'4px'}}>You're building <span style={{color:'#16a34a',fontWeight:'600'}}>meaningful relationships</span> and driving <span style={{color:'#16a34a',fontWeight:'600'}}>growth.</span></div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:'8px',background:'#fff',border:'1px solid #ecedf2',borderRadius:'10px',padding:'9px 14px',fontSize:'13px',color:'#475569'}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>{new Date().toLocaleDateString('en-US',{month:'long',year:'numeric'})}</div>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'16px',marginBottom:'20px'}}>
                {[
                  {label:'Conversations',value:(usage?usage.conversations.used:0).toLocaleString(),ic:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.4 8.4 0 0 1-9 8.5 8.5 8.5 0 0 1-3.8-.9L3 20l1.9-5.2A8.5 8.5 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z"/></svg>},
                  {label:'Read Rate',value:readRate+'%',ic:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>},
                  {label:'Messages Sent',value:totalSent.toLocaleString(),ic:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 2-7 20-4-9-9-4z"/><path d="M22 2 11 13"/></svg>},
                  {label:'Campaigns',value:campaigns.length.toLocaleString(),ic:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 11 18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>},
                ].map((k,i)=>(
                  <div key={i} style={{background:'#fff',borderRadius:'14px',padding:'20px',border:'1px solid #ecedf2',display:'flex',alignItems:'center',gap:'14px'}}>
                    <div style={{width:'48px',height:'48px',borderRadius:'14px',background:'#e9f7ee',display:'flex',alignItems:'center',justifyContent:'center',color:'#16a34a',flexShrink:0}}>{k.ic}</div>
                    <div><div style={{fontSize:'12px',color:'#64748b'}}>{k.label}</div><div style={{fontSize:'24px',fontWeight:'800',color:'#0f172a',lineHeight:'1.2'}}>{k.value}</div></div>
                  </div>
                ))}
              </div>

              <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:'20px',marginBottom:'20px'}}>
                <div style={{background:'#fff',borderRadius:'14px',padding:'20px',border:'1px solid #ecedf2'}}>
                  <div style={{fontSize:'15px',fontWeight:'700',color:'#0f172a',marginBottom:'14px'}}>Messages Over Time</div>
                  {dash&&dash.timeseries&&dash.timeseries.length>0?(
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={dash.timeseries} margin={{top:5,right:10,left:-12,bottom:0}}>
                        <defs><linearGradient id="gMsg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#16a34a" stopOpacity={0.25}/><stop offset="100%" stopColor="#16a34a" stopOpacity={0}/></linearGradient></defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" vertical={false}/>
                        <XAxis dataKey="day" tick={{fontSize:11,fill:'#94a3b8'}} axisLine={false} tickLine={false}/>
                        <YAxis tick={{fontSize:11,fill:'#94a3b8'}} axisLine={false} tickLine={false}/>
                        <Tooltip/>
                        <Area type="monotone" dataKey="count" stroke="#16a34a" strokeWidth={2.5} fill="url(#gMsg)" name="Messages"/>
                      </AreaChart>
                    </ResponsiveContainer>
                  ):<div style={{height:'250px',display:'flex',alignItems:'center',justifyContent:'center',color:'#94a3b8',fontSize:'13px'}}>No message activity yet</div>}
                </div>
                <div style={{borderRadius:'14px',padding:'22px',background:'linear-gradient(150deg,#0e3a25 0%,#16a34a 140%)',color:'#fff',display:'flex',flexDirection:'column'}}>
                  <div style={{fontSize:'16px',fontWeight:'700',marginBottom:'4px'}}>AI Assistant</div>
                  <div style={{fontSize:'12px',color:'rgba(255,255,255,0.8)',marginBottom:'16px'}}>Save time and grow faster with AI-powered tools.</div>
                  {[{t:'Generate Broadcast',d:'Create a new campaign',pg:'new-campaign'},{t:'Auto Replies',d:'Automate follow-ups',pg:'auto-replies'},{t:'Write Template',d:'Generate templates',pg:'templates'}].map((a,i)=>(
                    <div key={i} onClick={()=>setPage(a.pg)} style={{background:'rgba(255,255,255,0.1)',borderRadius:'10px',padding:'12px 14px',marginBottom:'10px',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <div><div style={{fontSize:'13px',fontWeight:'600'}}>{a.t}</div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.75)'}}>{a.d}</div></div>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1.4fr 1fr',gap:'20px',marginBottom:'20px'}}>
                <div style={{background:'#fff',borderRadius:'14px',padding:'20px',border:'1px solid #ecedf2'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'14px'}}>
                    <div style={{fontSize:'15px',fontWeight:'700',color:'#0f172a'}}>Recent Campaigns</div>
                    <span onClick={()=>setPage('campaigns')} style={{fontSize:'12px',color:'#16a34a',fontWeight:'600',cursor:'pointer'}}>View All</span>
                  </div>
                  {campaigns.length>0?(
                    <table style={s.table}><thead><tr><th style={s.th}>Campaign</th><th style={s.th}>Sent</th><th style={s.th}>Read</th><th style={s.th}>Status</th></tr></thead>
                    <tbody>{campaigns.slice(-5).reverse().map(c=>(<tr key={c.id}><td style={s.td}>{c.name}</td><td style={s.td}>{c.sent}</td><td style={s.td}>{c.read_count}</td><td style={s.td}><span style={s.badge(c.status==='Completed'?'#16a34a':'#ea580c')}>{c.status}</span></td></tr>))}</tbody></table>
                  ):<div style={s.emptyBox}><div>No campaigns yet</div><br/><button style={s.btn()} onClick={()=>setPage('new-campaign')}>+ New Campaign</button></div>}
                </div>
                <div style={{background:'#fff',borderRadius:'14px',padding:'20px',border:'1px solid #ecedf2'}}>
                  <div style={{fontSize:'15px',fontWeight:'700',color:'#0f172a',marginBottom:'14px'}}>Message Status</div>
                  {dash&&dash.statusBreakdown&&dash.statusBreakdown.length>0?(
                    <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                      <ResponsiveContainer width="48%" height={170}>
                        <PieChart><Pie data={dash.statusBreakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={72} paddingAngle={2}>
                          {dash.statusBreakdown.map((e,i)=><Cell key={i} fill={({read:'#16a34a',delivered:'#4ade80',sent:'#86efac',failed:'#f87171',scheduled:'#fbbf24'})[e.name]||'#cbd5e1'}/>)}
                        </Pie><Tooltip/></PieChart>
                      </ResponsiveContainer>
                      <div style={{flex:1}}>{dash.statusBreakdown.map((e,i)=>(<div key={i} style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'9px',fontSize:'12px'}}><span style={{width:'10px',height:'10px',borderRadius:'3px',background:({read:'#16a34a',delivered:'#4ade80',sent:'#86efac',failed:'#f87171',scheduled:'#fbbf24'})[e.name]||'#cbd5e1'}}/><span style={{color:'#475569',textTransform:'capitalize',flex:1}}>{e.name}</span><span style={{fontWeight:'700',color:'#0f172a'}}>{e.value.toLocaleString()}</span></div>))}</div>
                    </div>
                  ):<div style={{height:'170px',display:'flex',alignItems:'center',justifyContent:'center',color:'#94a3b8',fontSize:'13px'}}>No data yet</div>}
                </div>
              </div>

              {usage && (
              <div style={{background:'#fff',borderRadius:'14px',padding:'20px',border:'1px solid #ecedf2'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'18px',flexWrap:'wrap',gap:'8px'}}>
                  <div style={{fontSize:'15px',fontWeight:'700',color:'#0f172a'}}>Plan usage</div>
                  <div style={{display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap'}}>
                    <span style={{fontSize:'12px',fontWeight:'600',color:'#15803d',background:'#ecfdf3',padding:'4px 12px',borderRadius:'20px'}}>{usage.plan_name}</span>
                    {usage.next_billing&&<span style={{fontSize:'12px',color:'#64748b'}}>Next billing: {new Date(usage.next_billing).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'20px'}}>
                  {[{label:'Team members',u:usage.team.used,l:usage.team.limit},{label:'Campaigns',u:usage.campaigns.used,l:usage.campaigns.limit},{label:'Contacts',u:usage.contacts.used,l:usage.contacts.limit},{label:'Conversations',u:usage.conversations.used,l:usage.conversations.limit}].map((m,i)=>{const pct=m.l>0?Math.min(100,Math.round(m.u/m.l*100)):0;const bar=pct>=90?'#dc2626':pct>=75?'#ea580c':P;return (
                    <div key={i}>
                      <div style={{display:'flex',justifyContent:'space-between',marginBottom:'6px'}}><span style={{fontSize:'12px',color:'#64748b'}}>{m.label}</span><span style={{fontSize:'12px',fontWeight:'600',color:'#0f172a'}}>{m.u.toLocaleString()} / {m.l.toLocaleString()}</span></div>
                      <div style={{height:'7px',background:'#eef1f4',borderRadius:'4px',overflow:'hidden'}}><div style={{height:'100%',width:pct+'%',background:bar,borderRadius:'4px'}}/></div>
                    </div>
                  );})}
                </div>
              </div>
              )}
            </div>
          )}

          {page==='wa-overview'&&(
            <div>
              <div style={s.pageTitle}>WhatsApp Overview</div>
              <div style={s.kpiGrid}>
                <div style={s.kpiCard}><div style={s.kpiIcon('#f0fdf4')}><span style={{color:P}}>G</span></div><div><div style={{...s.kpiNum,color:P}}>{contacts.length}</div><div style={s.kpiLabel}>Total Contacts</div></div></div>
                <div style={s.kpiCard}><div style={s.kpiIcon('#f0fdf4')}><span style={{color:'#16a34a'}}>A</span></div><div><div style={{...s.kpiNum,color:'#16a34a'}}>{contacts.filter(c=>c.status).length}</div><div style={s.kpiLabel}>Active</div></div></div>
                <div style={s.kpiCard}><div style={s.kpiIcon('#fff7ed')}><span style={{color:'#ea580c'}}>R</span></div><div><div style={{...s.kpiNum,color:'#ea580c'}}>{readRate}%</div><div style={s.kpiLabel}>Read Rate</div></div></div>
                <div style={s.kpiCard}><div style={s.kpiIcon('#fef2f2')}><span style={{color:'#dc2626'}}>C</span></div><div><div style={{...s.kpiNum,color:'#dc2626'}}>{campaigns.length}</div><div style={s.kpiLabel}>Campaigns</div></div></div>
              </div>
              <div style={s.card}>
                <div style={s.cardTitle}>Audience Growth</div>
                {contacts.length>0?(
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={[{month:'Now',total:contacts.length}]}>
                      <CartesianGrid strokeDasharray="3 3"/><XAxis dataKey="month"/><YAxis/><Tooltip/><Legend/>
                      <Line type="monotone" dataKey="total" stroke={P} strokeWidth={2} name="Contacts"/>
                    </LineChart>
                  </ResponsiveContainer>
                ):<div style={s.emptyBox}><div>No data yet.</div></div>}
              </div>
            </div>
          )}

          {page==='contact-lists'&&(
            <div>
              <div style={s.row}>
                <div style={s.pageTitle}>Contact Lists</div>
                <button style={s.btn()} onClick={async()=>{
                  const t=prompt('List name:');
                  if(!t) return;
                  const res=await fetch(`${API}/contact-lists`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({clientId:user.clientId,title:t})});
                  const d=await res.json();
                  if(d.success) setContactLists(prev=>[...prev,d.list]);
                }}>+ Add New</button>
              </div>
              <div style={s.card}>
                {contactLists.length>0?(
                  <table style={s.table}>
                    <thead><tr><th style={s.th}>#</th><th style={s.th}>Title</th><th style={s.th}>Action</th></tr></thead>
                    <tbody>{contactLists.map((l,i)=>(<tr key={l.id}><td style={s.td}>{i+1}</td><td style={s.td}><b>{l.title}</b></td>
                    <td style={s.td}><button style={s.actionBtn('#dc2626')} onClick={async()=>{
                      await fetch(`${API}/contact-lists/${l.id}`,{method:'DELETE'});
                      setContactLists(prev=>prev.filter(x=>x.id!==l.id));
                    }}>Del</button></td></tr>))}</tbody>
                  </table>
                ):<div style={s.emptyBox}><div>No lists yet.</div></div>}
              </div>
            </div>
          )}

          {page==='segments'&&(
            <div>
              <div style={s.row}>
                <div style={s.pageTitle}>Segments</div>
                <button style={s.btn()} onClick={async()=>{
                  const n=prompt('Segment name:');
                  if(!n) return;
                  const res=await fetch(`${API}/segments`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({clientId:user.clientId,name:n})});
                  const d=await res.json();
                  if(d.success) setSegments(prev=>[...prev,d.segment]);
                }}>+ Add New</button>
              </div>
              <div style={s.card}>
                {segments.length>0?(
                  <table style={s.table}>
                    <thead><tr><th style={s.th}>#</th><th style={s.th}>Name</th><th style={s.th}>Status</th><th style={s.th}>Action</th></tr></thead>
                    <tbody>{segments.map((seg,i)=>(<tr key={seg.id}><td style={s.td}>{i+1}</td><td style={s.td}><b>{seg.name}</b></td>
                    <td style={s.td}><Toggle on={seg.active} onToggle={()=>setSegments(prev=>prev.map(s=>s.id===seg.id?{...s,active:!s.active}:s))}/></td>
                    <td style={s.td}><button style={s.actionBtn('#dc2626')} onClick={async()=>{
                      await fetch(`${API}/segments/${seg.id}`,{method:'DELETE'});
                      setSegments(prev=>prev.filter(x=>x.id!==seg.id));
                    }}>Del</button></td></tr>))}</tbody>
                  </table>
                ):<div style={s.emptyBox}><div>No segments yet.</div></div>}
              </div>
            </div>
          )}

          {page==='contacts'&&(
            <div>
              <div style={s.row}>
                <div style={s.pageTitle}>Contacts <span style={{fontSize:'14px',color:'#6b7280',fontWeight:'400'}}>({contacts.length} total)</span></div>
                <div style={{display:'flex',gap:'8px'}}>
                 <div style={{position:'relative'}}>
  <button style={s.btn('#22c55e')} onClick={()=>setShowBulkMenu(!showBulkMenu)}>Bulk Action</button>
  {showBulkMenu&&(
    <div style={{position:'absolute',top:'40px',left:0,background:'#fff',borderRadius:'8px',boxShadow:'0 4px 20px rgba(0,0,0,0.15)',zIndex:100,minWidth:'180px',padding:'8px 0'}}>
      {['Add List','Remove List','Add Segment','Remove Segment','Add Blacklist','Remove Blacklist','Delete Contacts'].map((item,i)=>(
        <div key={i} onClick={async()=>{
          if(item==='Delete Contacts'){
            if(selectedContacts.length===0){alert('Select contacts first');return;}
            for(const id of selectedContacts){ await fetch(`${API}/contacts/${id}`,{method:'DELETE'}); }
            setContacts(prev=>prev.filter(c=>!selectedContacts.includes(c.id)));
            setSelectedContacts([]);
          } else if(item==='Add List'){
            if(selectedContacts.length===0){alert('Select contacts first');return;}
            const listId=prompt('Select list ID - Available: '+contactLists.map(l=>l.id+':'+l.title).join(', '));
            if(listId) setContacts(prev=>prev.map(c=>selectedContacts.includes(c.id)?{...c,list_id:Number(listId)}:c));
          } else if(item==='Remove List'){
            if(selectedContacts.length===0){alert('Select contacts first');return;}
            setContacts(prev=>prev.map(c=>selectedContacts.includes(c.id)?{...c,list_id:null}:c));
          } else if(item==='Add Segment'){
            if(selectedContacts.length===0){alert('Select contacts first');return;}
            const segId=prompt('Select segment ID - Available: '+segments.map(s=>s.id+':'+s.name).join(', '));
            if(segId) setContacts(prev=>prev.map(c=>selectedContacts.includes(c.id)?{...c,segment_id:Number(segId)}:c));
          } else if(item==='Remove Segment'){
            if(selectedContacts.length===0){alert('Select contacts first');return;}
            setContacts(prev=>prev.map(c=>selectedContacts.includes(c.id)?{...c,segment_id:null}:c));
          }
          setShowBulkMenu(false);
        }} style={{padding:'10px 16px',cursor:'pointer',fontSize:'13px',color:item==='Delete Contacts'?'#dc2626':'#374151',borderBottom:'1px solid #f3f4f6'}}
        onMouseOver={e=>e.target.style.background='#f0fdf4'} onMouseOut={e=>e.target.style.background='transparent'}>
          {item}
        </div>
      ))}
    </div>
  )}
</div>
                  <button style={s.btn()} onClick={()=>setPage('add-contact')}>+ Add New</button>
                  <button style={s.btn('#16a34a')} onClick={()=>setPage('import-contacts')}>Import XLSX</button>
                </div>
              </div>
              <div style={s.card}>
                <div style={s.row}>
                  <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                    <select style={s.select} value={perPage} onChange={e=>{setPerPage(Number(e.target.value));setContactPage(1);}}>
                      <option value="10">10</option><option value="25">25</option><option value="50">50</option><option value="100">100</option><option value="500">500</option>
                    </select>
                    <span style={{fontSize:'13px',color:'#6b7280'}}>Per Page</span>
                  </div>
                  <input style={s.searchInput} placeholder="Search name or phone..." value={search} onChange={e=>{setSearch(e.target.value);setContactPage(1);}}/>
                </div>
                {pagedContacts.length>0?(
                  <div>
                    <div style={{overflowX:'auto'}}>
                      <table style={s.table}>
                        <thead><tr><th style={s.th}>#</th><th style={s.th}>Name</th><th style={s.th}>Phone</th><th style={s.th}>Country</th><th style={s.th}>Contact List</th><th style={s.th}>Segment</th><th style={s.th}>Status</th><th style={s.th}>Action</th></tr></thead>
                        <tbody>{pagedContacts.map((c,i)=>(
                          <tr key={c.id}>
                            <td style={s.td}>{(contactPage-1)*perPage+i+1}</td>
                            <td style={s.td}><b>{c.name}</b></td>
                            <td style={s.td}>{c.phone}</td>
                            <td style={s.td}>{c.country}</td>
                            <td style={s.td}><span style={{fontSize:'12px',color:P}}>{contactLists.find(l=>l.id===c.list_id)?.title||'-'}</span></td>
                            <td style={s.td}><span style={{fontSize:'12px',color:'#6b7280'}}>{segments.find(seg=>seg.id===c.segment_id)?.name||'-'}</span></td>
                            <td style={s.td}><Toggle on={c.status} onToggle={()=>setContacts(prev=>prev.map(x=>x.id===c.id?{...x,status:!x.status}:x))}/></td>
                            <td style={s.td}><button style={s.actionBtn('#dc2626')} onClick={async()=>{await fetch(`${API}/contacts/${c.id}`,{method:'DELETE'});setContacts(prev=>prev.filter(x=>x.id!==c.id));}}>Del</button></td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:'16px',flexWrap:'wrap',gap:'8px'}}>
                      <div style={{fontSize:'13px',color:'#6b7280'}}>Showing {Math.min((contactPage-1)*perPage+1,filteredContacts.length)} to {Math.min(contactPage*perPage,filteredContacts.length)} of {filteredContacts.length}</div>
                      <div style={{display:'flex',gap:'6px'}}>
                        <button style={{...s.btnOut,padding:'6px 12px'}} disabled={contactPage===1} onClick={()=>setContactPage(1)}>First</button>
                        <button style={{...s.btnOut,padding:'6px 12px'}} disabled={contactPage===1} onClick={()=>setContactPage(p=>p-1)}>Prev</button>
                        <span style={{padding:'6px 12px',background:P,color:'#fff',borderRadius:'6px',fontSize:'13px'}}>{contactPage} / {totalPages}</span>
                        <button style={{...s.btnOut,padding:'6px 12px'}} disabled={contactPage>=totalPages} onClick={()=>setContactPage(p=>p+1)}>Next</button>
                        <button style={{...s.btnOut,padding:'6px 12px'}} disabled={contactPage>=totalPages} onClick={()=>setContactPage(totalPages)}>Last</button>
                      </div>
                    </div>
                  </div>
                ):<div style={s.emptyBox}><div>No contacts yet.</div></div>}
              </div>
            </div>
          )}

          {page==='add-contact'&&(
            <div>
              <div style={s.row}><div style={s.pageTitle}>Add Contact</div><button style={s.btnOut} onClick={()=>setPage('contacts')}>Back</button></div>
              <div style={s.card}>
                <label style={s.label}>Name</label>
                <input style={s.input} placeholder="Full Name" value={newContact.name} onChange={e=>setNewContact({...newContact,name:e.target.value})}/>
                <label style={s.label}>Phone (With Country Code)</label>
                <input style={s.input} placeholder="+966XXXXXXXXX" value={newContact.phone} onChange={e=>setNewContact({...newContact,phone:e.target.value})}/>
                <label style={s.label}>Contact List</label>
                <select style={{...s.input}} onChange={e=>setNewContact({...newContact,listId:e.target.value})}>
                  <option value="">Select List</option>
                  {contactLists.map(l=><option key={l.id} value={l.id}>{l.title}</option>)}
                </select>
                <label style={s.label}>Segment</label>
                <select style={{...s.input}} onChange={e=>setNewContact({...newContact,segmentId:e.target.value})}>
                  <option value="">Select Segment</option>
                  {segments.map(seg=><option key={seg.id} value={seg.id}>{seg.name}</option>)}
                </select>
                <label style={s.label}>Country</label>
                <select style={{...s.input}} value={newContact.country} onChange={e=>setNewContact({...newContact,country:e.target.value})}>
                  <option value="">Select Country</option>
                  <option value="Saudi Arabia">Saudi Arabia</option>
                  <option value="UAE">UAE</option>
                  <option value="India">India</option>
                  <option value="USA">USA</option>
                </select>
                <button style={s.btn()} onClick={async()=>{
                  if(!newContact.name||!newContact.phone){setStatus('Name and phone required.');setStatusOk(false);return;}
                  const res=await fetch(`${API}/contacts`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({clientId:user.clientId,contacts:[newContact]})});
                  const d=await res.json();
                  if(d.success){
                    const r=await fetch(`${API}/contacts?clientId=${user.clientId}`);
                    const rd=await r.json();
                    if(rd.success) setContacts(rd.contacts);
                    setStatus('Contact added!');setStatusOk(true);
                    setNewContact({name:'',phone:'',country:''});setPage('contacts');
                  }
                }}>Save Contact</button>
                {status&&<div style={s.statusMsg(statusOk)}>{status}</div>}
              </div>
            </div>
          )}

          {page==='import-contacts'&&(
            <div>
              <div style={s.row}>
                <div style={s.pageTitle}>Import Contacts</div>
                <button style={s.btnOut} onClick={()=>setPage('contacts')}>Back</button>
              </div>
              <div style={s.card}>
                <div style={{color:'#dc2626',fontSize:'13px',marginBottom:'12px'}}>
                  <b>Note:</b> Only .xlsx files accepted. Include phone numbers with country codes (e.g. 966XXXXXXXXX).{' '}
                  <span style={{color:P,cursor:'pointer',fontWeight:'600',textDecoration:'underline'}} onClick={()=>{const a=document.createElement('a');a.href='/sample-contacts.csv';a.download='sample-contacts.csv';a.click();}}>Download sample file here.</span>
                </div>
                <div style={s.uploadBox}>
                  <div style={{fontSize:'16px',fontWeight:'600',marginBottom:'8px'}}>Choose File (.xlsx or .csv)</div>
                  <input type="file" accept=".xlsx,.xls,.csv" onChange={handleCSVFile}/>
                </div>
              </div>
              {importPreview.length>0&&(
                <div style={s.card}>
                  <div style={{display:'flex',gap:'12px',alignItems:'center',marginBottom:'16px',flexWrap:'wrap'}}>
                    <div style={{fontSize:'13px',color:'#16a34a',fontWeight:'600'}}>{importPreview.length} contacts ready to import</div>
                    <select style={s.select} onChange={e=>{const v=e.target.value;setImportPreview(prev=>prev.map(x=>({...x,listId:v})));}}> 
                      <option value="">Apply List to All</option>
                      {contactLists.map(l=><option key={l.id} value={l.id}>{l.title}</option>)}
                    </select>
                    <select style={s.select} onChange={e=>{const v=e.target.value;setImportPreview(prev=>prev.map(x=>({...x,segmentId:v})));}}>
                      <option value="">Apply Segment to All</option>
                      {segments.map(seg=><option key={seg.id} value={seg.id}>{seg.name}</option>)}
                    </select>
                  </div>
                  <div style={{overflowX:'auto',overflowY:'auto',maxHeight:'400px'}}>
                    <table style={s.table}>
                      <thead><tr><th style={s.th}>#</th><th style={s.th}>Name</th><th style={s.th}>Phone</th><th style={s.th}>Contact List</th><th style={s.th}>Segment</th></tr></thead>
                      <tbody>{importPreview.map((c,i)=>(
                        <tr key={i}>
                          <td style={s.td}>{i+1}</td>
                          <td style={s.td}>{c.name}</td>
                          <td style={s.td}>{c.phone}</td>
                          <td style={s.td}>
                            <select style={s.select} value={c.listId||''} onChange={e=>setImportPreview(prev=>prev.map((x,xi)=>xi===i?{...x,listId:e.target.value}:x))}>
                              <option value="">Select List</option>
                              {contactLists.map(l=><option key={l.id} value={l.id}>{l.title}</option>)}
                            </select>
                          </td>
                          <td style={s.td}>
                            <select style={s.select} value={c.segmentId||''} onChange={e=>setImportPreview(prev=>prev.map((x,xi)=>xi===i?{...x,segmentId:e.target.value}:x))}>
                              <option value="">Select Segment</option>
                              {segments.map(seg=><option key={seg.id} value={seg.id}>{seg.name}</option>)}
                            </select>
                          </td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                  <button style={{...s.btn('#16a34a'),marginTop:'16px'}} onClick={doImport}>Import {importPreview.length} Contacts</button>
                </div>
              )}
            </div>
          )}

          {page==='send'&&(
            <div>
              <div style={s.pageTitle}>Send Message</div>
              <div style={s.card}>
                <label style={s.label}>Phone Number</label>
                <input style={s.input} placeholder="Phone with country code" value={phone} onChange={e=>setPhone(e.target.value)}/>
                <label style={s.label}>Message</label>
                <textarea style={s.textarea} placeholder="Type message..." value={message} onChange={e=>setMessage(e.target.value)}/>
                <button style={s.btn()} onClick={sendMessage} disabled={loading}>{loading?'Sending...':'Send Message'}</button>
                {status&&<div style={s.statusMsg(statusOk)}>{status}</div>}
              </div>
            </div>
          )}

          {page==='auto-replies'&&(
            <div>
              <div style={s.pageTitle}>Auto Replies</div>
              <div style={{...s.card,borderLeft:`4px solid ${P}`}}>
                <label style={s.label}>Keyword</label>
                <input style={s.input} placeholder="e.g. hello, price" value={newAutoReply.keyword} onChange={e=>setNewAutoReply({...newAutoReply,keyword:e.target.value})}/>
                <label style={s.label}>Reply Message</label>
                <textarea style={{...s.textarea,height:'80px'}} value={newAutoReply.reply} onChange={e=>setNewAutoReply({...newAutoReply,reply:e.target.value})}/>
                <button style={s.btn()} onClick={()=>{if(!newAutoReply.keyword||!newAutoReply.reply)return;fetch(`${API}/auto-replies`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({clientId:user.clientId,keyword:newAutoReply.keyword,reply:newAutoReply.reply})}).then(r=>r.json()).then(d=>{if(d.success){setAutoReplies(prev=>[...prev,d.autoReply]);setNewAutoReply({keyword:'',reply:''});}else{alert(d.error||'Failed to add');}});}}>+ Add</button>
              </div>
              <div style={s.card}>
                {autoReplies.length>0?(
                  <table style={s.table}>
                    <thead><tr><th style={s.th}>#</th><th style={s.th}>Keyword</th><th style={s.th}>Reply</th><th style={s.th}>Status</th><th style={s.th}>Action</th></tr></thead>
                    <tbody>{autoReplies.map(ar=>(<tr key={ar.id}><td style={s.td}>{ar.id}</td><td style={s.td}><b>{ar.keyword}</b></td><td style={s.td}>{ar.reply.substring(0,50)}</td><td style={s.td}><Toggle on={ar.active} onToggle={()=>fetch(`${API}/auto-replies/${ar.id}`,{method:'PATCH',headers:{'Content-Type':'application/json'},body:JSON.stringify({active:!ar.active})}).then(r=>r.json()).then(d=>{if(d.success)setAutoReplies(prev=>prev.map(r=>r.id===ar.id?{...r,active:!r.active}:r));})}/></td><td style={s.td}><button style={s.actionBtn('#dc2626')} onClick={()=>fetch(`${API}/auto-replies/${ar.id}`,{method:'DELETE'}).then(r=>r.json()).then(d=>{if(d.success)setAutoReplies(prev=>prev.filter(x=>x.id!==ar.id));})}>Del</button></td></tr>))}</tbody>
                  </table>
                ):<div style={s.emptyBox}><div>No auto replies yet.</div></div>}
              </div>
            </div>
          )}

          {page==='templates'&&(
            <div>
              <div style={s.row}>
                <div style={s.pageTitle}>Template Management</div>
                <div style={{display:'flex',gap:'8px'}}>
                  <button style={s.btn()} onClick={loadTemplates}>Load Templates</button>
                  <button style={s.btn('#1877f2')} onClick={()=>window.open('https://business.facebook.com/wa/manage/message-templates/','_blank')}>Create on Facebook</button>
                </div>
              </div>
              <div style={{...s.card,padding:'12px 20px',marginBottom:'16px'}}>
                <span style={{fontSize:'13px',color:'#6b7280'}}>Client: </span>
                <span style={{fontSize:'13px',fontWeight:'700',color:P}}>{user.clientName}</span>
              </div>
              {templates.length>0?(
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:'16px'}}>
                  {templates.map((t,i)=>(
                    <div key={i} style={{background:'white',borderRadius:'12px',padding:'20px',border:'1px solid #e5e7eb',boxShadow:'0 1px 3px rgba(0,0,0,0.08)'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'12px'}}>
                        <div style={{fontSize:'15px',fontWeight:'700',color:'#1e2139'}}>{t.name}</div>
                        <div style={{padding:'3px 10px',borderRadius:'20px',fontSize:'11px',fontWeight:'600',background:t.status==='APPROVED'?'#dcfce7':t.status==='PENDING'?'#fef9c3':'#fee2e2',color:t.status==='APPROVED'?'#16a34a':t.status==='PENDING'?'#ca8a04':'#dc2626'}}>{t.status}</div>
                      </div>
                      <div style={{fontSize:'12px',color:'#6b7280',marginBottom:'10px'}}>{t.category} - {t.language}</div>
                      {t.components&&t.components.map((comp,j)=>comp.text&&(
                        <div key={j} style={{background:'#f8fafc',borderRadius:'8px',padding:'10px',marginBottom:'6px',fontSize:'12px',color:'#374151'}}>
                          <span style={{fontWeight:'600',color:P}}>{comp.type}: </span>{comp.text}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ):(
                <div style={s.card}><div style={s.emptyBox}><div style={{marginBottom:'12px'}}>Click "Load Templates" to fetch from Meta</div><button style={s.btn('#1877f2')} onClick={()=>window.open('https://business.facebook.com/wa/manage/message-templates/','_blank')}>Create Template on Facebook</button></div></div>
              )}
            </div>
          )}

          {page==='new-campaign'&&(
            <div>
              <div style={s.pageTitle}>New Campaign</div>
              <div style={s.card}>
                <label style={s.label}>Campaign Name *</label>
                <input style={s.input} placeholder="Campaign name" value={newCampaign.name} onChange={e=>setNewCampaign({...newCampaign,name:e.target.value})}/>
                <label style={s.label}>Select Contact List *</label>
                <select style={{...s.input}} value={newCampaign.listId||''} onChange={e=>setNewCampaign({...newCampaign,listId:e.target.value,segmentId:''})}>
                  <option value="">-- Select Contact List --</option>
                  {contactLists.map(l=><option key={l.id} value={l.id}>{l.title}</option>)}
                </select>
                <label style={s.label}>OR Select Segment</label>
                <select style={{...s.input}} value={newCampaign.segmentId||''} onChange={e=>setNewCampaign({...newCampaign,segmentId:e.target.value,listId:''})}>
                  <option value="">-- Select Segment --</option>
                  {segments.map(seg=><option key={seg.id} value={seg.id}>{seg.name}</option>)}
                </select>
                <label style={s.label}>Select Template *</label>
                <div style={{display:'flex',gap:'8px',marginBottom:'12px'}}>
                  <select style={{...s.input,marginBottom:0,flex:1}} value={newCampaign.templateName||''} onChange={e=>setNewCampaign({...newCampaign,templateName:e.target.value})}>
                    <option value="">-- Select Template --</option>
                    {templates.map((t,i)=><option key={i} value={t.name}>{t.name} ({t.status})</option>)}
                  </select>
                  <button style={s.btn('#6b7280')} onClick={loadTemplates}>Load</button>
                </div>
                {newCampaign.templateName&&templates.find(t=>t.name===newCampaign.templateName)?.components?.some(c=>c.type==='HEADER'&&c.format==='IMAGE')&&(<div><label style={s.label}>Header Image URL (Required) *</label><input style={s.input} placeholder="https://example.com/image.jpg" value={newCampaign.headerImageUrl||''} onChange={e=>setNewCampaign({...newCampaign,headerImageUrl:e.target.value})} readOnly/><input type="file" accept="image/*" style={{marginTop:'8px',width:'100%'}} onChange={async e=>{const f=e.target.files[0];if(!f)return;const fd=new FormData();fd.append('image',f);const r=await fetch(`${API}/upload-image`,{method:'POST',body:fd});const d=await r.json();if(d.success)setNewCampaign(prev=>({...prev,headerImageUrl:d.url}));}}/>  </div>)}
                {newCampaign.templateName&&templates.find(t=>t.name===newCampaign.templateName)&&(()=>{
                  const tpl=templates.find(t=>t.name===newCampaign.templateName);
                  const comps=(tpl&&tpl.components)||[];
                  const header=comps.find(c=>c.type==='HEADER');
                  const body=comps.find(c=>c.type==='BODY');
                  const footer=comps.find(c=>c.type==='FOOTER');
                  const btns=(comps.find(c=>c.type==='BUTTONS')&&comps.find(c=>c.type==='BUTTONS').buttons)||[];
                  const imgUrl=newCampaign.headerImageUrl||(header&&header.format==='IMAGE'&&header.example&&header.example.header_handle&&header.example.header_handle[0])||'';
                  const btnIcon=(t)=>t==='PHONE_NUMBER'?'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.7 2.34a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.74.34 1.53.57 2.34.7A2 2 0 0 1 22 16.92z':t==='URL'?'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71':'M9 17l-5-5 5-5M4 12h12a4 4 0 0 1 0 8h-1';
                  return (
                  <div style={{marginBottom:'16px'}}>
                    <div style={{fontWeight:'600',color:'#16a34a',marginBottom:'8px',fontSize:'13px'}}>Template Preview:</div>
                    <div style={{maxWidth:'330px',background:'#fff',borderRadius:'10px',boxShadow:'0 1px 4px rgba(0,0,0,0.14)',overflow:'hidden',border:'1px solid #e5e7eb'}}>
                      {header&&header.format==='IMAGE'&&imgUrl&&<img src={imgUrl} alt="header" style={{width:'100%',display:'block',maxHeight:'190px',objectFit:'cover'}}/>}
                      <div style={{padding:'10px 12px'}}>
                        {header&&header.format==='TEXT'&&header.text&&<div style={{fontWeight:'700',color:'#111b21',fontSize:'14px',marginBottom:'4px'}}>{header.text}</div>}
                        {body&&body.text&&<div style={{color:'#111b21',fontSize:'13px',whiteSpace:'pre-wrap',lineHeight:'1.45'}}>{body.text}</div>}
                        {footer&&footer.text&&<div style={{color:'#8696a0',fontSize:'11px',marginTop:'7px'}}>{footer.text}</div>}
                      </div>
                      {btns.length>0&&<div>
                        {btns.map((b,i)=>(<div key={i} style={{borderTop:'1px solid #eef0f1',padding:'11px',textAlign:'center',color:'#00a5f4',fontSize:'14px',fontWeight:'500',display:'flex',alignItems:'center',justifyContent:'center',gap:'7px',cursor:'default'}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={btnIcon(b.type)}/></svg>{b.text}</div>))}
                      </div>}
                    </div>
                  </div>
                  );
                })()}
                <div style={{background:'#f0fdf4',borderRadius:'8px',padding:'12px',marginBottom:'16px',fontSize:'13px',color:'#374151'}}>
                  <b>Recipients: </b>
                  {newCampaign.listId ? contacts.filter(c=>c.list_id==newCampaign.listId).length+' contacts from selected list' :
                   newCampaign.segmentId ? contacts.filter(c=>c.segment_id==newCampaign.segmentId).length+' contacts from selected segment' :
                   'Select a list or segment above'}
                </div>
                <label style={s.label}>Schedule (optional)</label>
                <div style={{display:'flex',gap:'8px',marginBottom:'12px'}}>
                  <select style={{...s.input,marginBottom:0,flex:1}} value={newCampaign.scheduleType||'immediate'} onChange={e=>setNewCampaign({...newCampaign,scheduleType:e.target.value})}>
                    <option value='immediate'>Send Now</option>
                    <option value='scheduled'>Schedule for Later</option>
                  </select>
                </div>
                {newCampaign.scheduleType==='scheduled'&&<div><label style={s.label}>Date & Time</label><input type='datetime-local' style={{...s.input}} value={newCampaign.scheduledAt||''} onChange={e=>setNewCampaign({...newCampaign,scheduledAt:e.target.value})}/></div>}
                <button style={{...s.btn(),width:'100%',justifyContent:'center',padding:'12px'}} onClick={sendBulkCampaign} disabled={loading||(!newCampaign.listId&&!newCampaign.segmentId)||!newCampaign.templateName||!newCampaign.name}>
                  {loading?'Sending... please wait':newCampaign.scheduleType==='scheduled'?'Schedule Campaign':'Send Campaign'}
                </button>
                {status&&<div style={s.statusMsg(statusOk)}>{status}</div>}
              </div>
            </div>
          )}

          {page==='campaigns'&&(
            <div>
              <div style={s.row}><div style={s.pageTitle}>Campaigns</div><button style={s.btn()} onClick={()=>setPage('new-campaign')}>+ New</button></div>
              <div style={s.card}>
                {campaigns.length>0?(
                  <table style={s.table}>
                    <thead><tr><th style={s.th}>#</th><th style={s.th}>Name</th><th style={s.th}>Date</th><th style={s.th}>Sent</th><th style={s.th}>Read</th><th style={s.th}>Status</th><th style={s.th}>Action</th></tr></thead>
                    <tbody>{campaigns.map(c=>(<tr key={c.id}><td style={s.td}>{c.id}</td><td style={s.td}><b>{c.name}</b></td><td style={s.td}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : "-"}</td><td style={s.td}>{c.sent}</td><td style={s.td}>{c.read_count||0}</td><td style={s.td}><span style={s.badge(c.status==='Scheduled'?'#f59e0b':'#16a34a')}>{c.status}</span></td><td style={s.td}><button style={{...s.btn('#22c55e'),padding:'4px 12px',fontSize:'12px'}} onClick={()=>setSelectedCampaign(c)}>Details</button></td></tr>))}</tbody>
                  </table>
                ):<div style={s.emptyBox}><div>No campaigns yet</div><br/><button style={s.btn()} onClick={()=>setPage('new-campaign')}>+ Create</button></div>}
              </div>
            </div>
          )}

          {selectedCampaign&&(
            <CampaignDetails campaign={selectedCampaign} onClose={()=>setSelectedCampaign(null)} API={API} clientId={user.clientId} />
          )}

          {page==='inbox'&&(
            <div style={{display:'flex',gap:'16px',height:'calc(100vh - 140px)'}}>
              <div style={{width:'320px',background:'#fff',borderRadius:'12px',border:'1px solid #e5e7eb',overflowY:'auto',flexShrink:0}}>
                <div style={{padding:'16px',borderBottom:'1px solid #e5e7eb',fontWeight:'700',fontSize:'16px',color:'#1e1b4b'}}>Conversations</div>
                {Object.entries(inbox.reduce((acc,m)=>{acc[m.from_phone]=acc[m.from_phone]||[];acc[m.from_phone].push(m);return acc},{})).length>0?
                  Object.entries(inbox.reduce((acc,m)=>{acc[m.from_phone]=acc[m.from_phone]||[];acc[m.from_phone].push(m);return acc},{})).map(([phone,msgs])=>(
                    <div key={phone} onClick={()=>{ setSelectedConvo(phone); fetch(`${API}/mark-read`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({clientId:user.clientId,phone})}); }} style={{padding:'14px 16px',borderBottom:'1px solid #f3f4f6',cursor:'pointer',background:selectedConvo===phone?'#f0fdf4':'#fff',display:'flex',alignItems:'center',gap:'12px'}}>
                      <div style={{width:'40px',height:'40px',borderRadius:'50%',background:'#22c55e',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:'700',fontSize:'16px',flexShrink:0}}>{contacts.find(c=>c.phone===phone)?.name?.[0]||phone[0]}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:'600',color:'#1e1b4b',fontSize:'14px'}}>{contacts.find(c=>c.phone===phone)?.name||phone}</div>
                        <div style={{fontSize:'12px',color:'#6b7280',marginTop:'2px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{msgs[0].message}</div>
                      </div>
                      <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:'4px',flexShrink:0}}>
                        <div style={{fontSize:'10px',color:'#9ca3af'}}>{new Date(msgs[0].created_at).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}</div>
                        {msgs.filter(m=>m.direction==='inbound'&&!m.is_read).length>0&&<span style={{background:'#ef4444',color:'#fff',borderRadius:'10px',padding:'1px 6px',fontSize:'11px',fontWeight:'700'}}>{msgs.filter(m=>m.direction==='inbound'&&!m.is_read).length}</span>}
                      </div>
                    </div>
                  ))
                :<div style={{padding:'40px',textAlign:'center',color:'#9ca3af'}}>No conversations yet</div>}
              </div>
              <div style={{flex:1,background:'#fff',borderRadius:'12px',border:'1px solid #e5e7eb',display:'flex',flexDirection:'column'}}>
                {selectedConvo?(
                  <>
                    <div style={{padding:'14px 16px',borderBottom:'1px solid #e5e7eb',display:'flex',alignItems:'center',gap:'12px'}}>
                      <div style={{width:'36px',height:'36px',borderRadius:'50%',background:'#22c55e',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:'700'}}>{contacts.find(c=>c.phone===selectedConvo)?.name?.[0]||selectedConvo[0]}</div>
                      <div>
                        <div style={{fontWeight:'600',color:'#1e1b4b'}}>{contacts.find(c=>c.phone===selectedConvo)?.name||selectedConvo}</div>
                        <div style={{fontSize:'12px',color:'#6b7280'}}>{selectedConvo}</div>{(()=>{const ib=inbox.filter(m=>m.from_phone===selectedConvo&&m.direction==='inbound');if(ib.length===0)return <div style={{marginTop:'4px',fontSize:'11px',fontWeight:'600',color:'#b45309',background:'#fef3c7',display:'inline-block',padding:'2px 8px',borderRadius:'10px'}}>Window closed - Template only</div>;const last=Math.max(...ib.map(m=>new Date(m.created_at).getTime()));const hl=24-(Date.now()-last)/3600000;if(hl>0){const h=Math.floor(hl),mm=Math.floor((hl-h)*60);return <div style={{marginTop:'4px',fontSize:'11px',fontWeight:'600',color:'#15803d',background:'#dcfce7',display:'inline-block',padding:'2px 8px',borderRadius:'10px'}}>Window open - {h}h {mm}m left</div>;}return <div style={{marginTop:'4px',fontSize:'11px',fontWeight:'600',color:'#b45309',background:'#fef3c7',display:'inline-block',padding:'2px 8px',borderRadius:'10px'}}>Window closed - Template only</div>;})()}
                      </div>
                      <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:'8px'}}>
                        <span style={{fontSize:'12px',color:'#6b7280'}}>Assigned to:</span>
                        <select value={(assignments.find(a=>a.phone===selectedConvo)||{}).assigned_to||''} onChange={async e=>{const v=e.target.value;await fetch(API+'/assign',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({clientId:user.clientId,phone:selectedConvo,assignedTo:v?parseInt(v):null})});loadAssignments();}} style={{padding:'6px 10px',borderRadius:'8px',border:'1px solid #d1d5db',fontSize:'13px',color:'#374151',background:'#fff',cursor:'pointer'}}>
                          <option value=''>Unassigned</option>
                          {team.filter(m=>m.status).map(m=>(<option key={m.id} value={m.id}>{m.name}</option>))}
                        </select>
                      </div>
                    </div>
                    <div style={{flex:1,overflowY:'auto',padding:'16px',display:'flex',flexDirection:'column',gap:'8px'}}>
                      {inbox.filter(m=>m.from_phone===selectedConvo).slice().reverse().map(m=>(
                        <div key={m.id} style={{display:'flex',justifyContent:m.direction==='outbound'?'flex-end':'flex-start'}}>
                          <div style={{maxWidth:'65%',padding:'10px 14px',borderRadius:'12px',background:m.direction==='outbound'?'#22c55e':'#f3f4f6',color:m.direction==='outbound'?'#fff':'#1f2937',fontSize:'13px'}}>
                            {m.message_type==="image"?<img src={m.message} style={{maxWidth:"100%",borderRadius:"8px",marginBottom:"4px"}} alt="img"/>:m.message_type==="document"?<a href={m.message} target="_blank" style={{color:"inherit",textDecoration:"underline",fontSize:"12px"}}>📄 {m.message.split("/").pop()}</a>:<span>{m.message}</span>}
                            <div style={{fontSize:'10px',opacity:'0.7',marginTop:'4px',textAlign:'right'}}>{new Date(m.created_at).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{padding:'12px 16px',borderTop:'1px solid #e5e7eb'}}>
                      {(()=>{const ib=inbox.filter(m=>m.from_phone===selectedConvo&&m.direction==='inbound');const last=ib.length?Math.max(...ib.map(m=>new Date(m.created_at).getTime())):0;const winOpen=!!last&&(24-(Date.now()-last)/3600000)>0;if(winOpen){return (<><div style={{display:'flex',gap:'8px',marginBottom:'8px'}}>
                        <label style={{cursor:'pointer',padding:'8px 12px',background:'#f3f4f6',borderRadius:'8px',fontSize:'13px',color:'#374151',display:'flex',alignItems:'center',gap:'4px'}}>
                          <span>Image</span>
                          <input type="file" accept="image/*" style={{display:'none'}} onChange={async e=>{const f=e.target.files[0];if(!f)return;const fd=new FormData();fd.append('file',f);fd.append('clientId',user.clientId);fd.append('phone',selectedConvo);fd.append('fileType','image');const r=await fetch(`${API}/send-attachment`,{method:'POST',body:fd});const d=await r.json();if(d.success){const r2=await fetch(`${API}/messages?clientId=${user.clientId}`);const d2=await r2.json();if(d2.success)setInbox(d2.messages);}e.target.value='';}}/>
                        </label>
                        <label style={{cursor:'pointer',padding:'8px 12px',background:'#f3f4f6',borderRadius:'8px',fontSize:'13px',color:'#374151',display:'flex',alignItems:'center',gap:'4px'}}>
                          <span>Document</span>
                          <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" style={{display:'none'}} onChange={async e=>{const f=e.target.files[0];if(!f)return;const fd=new FormData();fd.append('file',f);fd.append('clientId',user.clientId);fd.append('phone',selectedConvo);fd.append('fileType','document');const r=await fetch(`${API}/send-attachment`,{method:'POST',body:fd});const d=await r.json();if(d.success){const r2=await fetch(`${API}/messages?clientId=${user.clientId}`);const d2=await r2.json();if(d2.success)setInbox(d2.messages);}e.target.value='';}}/>
                        </label>
                      </div>
                      <div style={{display:'flex',gap:'8px'}}>
                        <input style={{...s.input,marginBottom:0,flex:1}} placeholder="Type a reply..." value={replyMsg||''} onChange={e=>setReplyMsg(e.target.value)} onKeyDown={async e=>{if(e.key==='Enter'&&replyMsg){const r=await fetch(`${API}/reply-message`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({clientId:user.clientId,phone:selectedConvo,message:replyMsg})});const d=await r.json();if(d.success){setReplyMsg('');const r2=await fetch(`${API}/messages?clientId=${user.clientId}`);const d2=await r2.json();if(d2.success)setInbox(d2.messages);}}}}/>
                        <button style={s.btn()} onClick={async()=>{if(!replyMsg)return;const r=await fetch(`${API}/reply-message`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({clientId:user.clientId,phone:selectedConvo,message:replyMsg})});const d=await r.json();if(d.success){setReplyMsg('');const r2=await fetch(`${API}/messages?clientId=${user.clientId}`);const d2=await r2.json();if(d2.success)setInbox(d2.messages);}}}>Send</button>
                      </div></>);}return (<div style={{padding:'12px 14px',background:'#fef3c7',border:'1px solid #fde68a',borderRadius:'8px',fontSize:'13px',color:'#92400e',lineHeight:'1.5'}}>24-hour window closed. Free-form replies cannot be sent - you can only send an approved template message.</div>);})()}
                    </div>
                  </>
                ):(
                  <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'#9ca3af',fontSize:'14px'}}>Select a conversation to start chatting</div>
                )}
              </div>
            </div>
          )}

          {page==='team'&&(
            <div>
              <div style={s.row}><div style={s.pageTitle}>Team Member</div><button style={s.btn()} onClick={()=>setShowAddMember(!showAddMember)}>+ Add</button></div>
              {showAddMember&&(
                <div style={{...s.card,borderLeft:`4px solid ${P}`}}>
                  <input style={s.input} placeholder="Full Name" value={newMember.name} onChange={e=>setNewMember({...newMember,name:e.target.value})}/>
                  <input style={s.input} placeholder="Email" value={newMember.email} onChange={e=>setNewMember({...newMember,email:e.target.value})}/>
                  <input style={s.input} type="text" placeholder="Password (blank = auto-generate)" value={newMember.password} onChange={e=>setNewMember({...newMember,password:e.target.value})}/>
                  <select style={{...s.input}} value={newMember.role} onChange={e=>setNewMember({...newMember,role:e.target.value})}><option>Agent</option><option>Manager</option><option>Admin</option></select>
                  <button style={s.btn()} onClick={()=>{if(!newMember.name||!newMember.email)return;fetch(API+'/team-members',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({clientId:user.clientId,name:newMember.name,email:newMember.email,role:newMember.role||'Agent',password:newMember.password})}).then(r=>r.json()).then(d=>{if(!d.success){alert(d.error||'Add failed');return;}alert('Member added!\nEmail: '+d.member.email+'\nPassword: '+d.tempPassword+'\n\nShare these login details.');setNewMember({name:'',email:'',role:'Agent',password:''});setShowAddMember(false);loadTeam();});}}>Add</button>
                </div>
              )}
              <div style={s.card}>
                <table style={s.table}>
                  <thead><tr><th style={s.th}>#</th><th style={s.th}>Name</th><th style={s.th}>Email</th><th style={s.th}>Role</th><th style={s.th}>Status</th><th style={s.th}>Action</th></tr></thead>
                  <tbody>{team.map(m=>(<tr key={m.id}><td style={s.td}>{m.id}</td><td style={s.td}><b>{m.name}</b></td><td style={s.td}>{m.email}</td><td style={s.td}><span style={s.badge(m.role==='Admin'?P:'#6b7280')}>{m.role}</span></td><td style={s.td}><Toggle on={m.status} onToggle={()=>fetch(API+'/team-members/'+m.id,{method:'PATCH',headers:{'Content-Type':'application/json'},body:JSON.stringify({clientId:user.clientId,status:!m.status})}).then(()=>loadTeam())}/></td><td style={s.td}><button style={s.actionBtn('#dc2626')} onClick={()=>m.role!=='Admin'&&window.confirm('Delete this member?')&&fetch(API+'/team-members/'+m.id+'?clientId='+user.clientId,{method:'DELETE'}).then(()=>loadTeam())}>Del</button></td></tr>))}</tbody>
                </table>
              </div>
            </div>
          )}

          {page==='ticket'&&(<div><div style={s.pageTitle}>My Ticket</div><div style={s.card}><div style={s.emptyBox}><div>No tickets yet.</div></div></div></div>)}
          {page==='ai-chat'&&<AIChatPage API={API} clientId={user.clientId} P={P} s={s}/>}

          {page==='settings-profile'&&(
            <div>
              <div style={s.pageTitle}>Profile Settings</div>
              <div style={s.card}>
                <label style={s.label}>Full Name</label><input style={s.input} defaultValue={user.name}/>
                <label style={s.label}>Email</label><input style={s.input} defaultValue={user.email}/>
                <label style={s.label}>Company</label><input style={s.input} defaultValue={user.clientName}/>
                <button style={s.btn()}>Save Changes</button>
              </div>
            </div>
          )}

          {page==='settings-billing'&&(
            <div><div style={s.pageTitle}>Billing</div>
            <div style={s.card}><div style={{background:'#f0fdf4',borderRadius:'10px',padding:'20px'}}><div style={{fontSize:'16px',fontWeight:'700',color:P}}>Advance Plan</div><div style={{fontSize:'13px',color:'#6b7280',marginTop:'4px'}}>Next billing: 2026-07-15</div></div></div></div>
          )}

          {page==='settings-api'&&(
            <div>
              <div style={s.pageTitle}>API Keys</div>
              <div style={s.card}>
                <div style={{background:'#1e2139',borderRadius:'8px',padding:'16px',marginBottom:'12px'}}>
                  <div style={{fontSize:'11px',color:'#8b9dc3',marginBottom:'4px'}}>PHONE NUMBER ID</div>
                  <div style={{fontSize:'12px',color:'#34d399'}}>{user.phoneId}</div>
                </div>
                <div style={{background:'#1e2139',borderRadius:'8px',padding:'16px'}}>
                  <div style={{fontSize:'11px',color:'#8b9dc3',marginBottom:'4px'}}>WABA ID</div>
                  <div style={{fontSize:'12px',color:'#34d399'}}>{user.wabaId}</div>
                </div>
              </div>
            </div>
          )}

          {page==='flows'&&(
            selectedFlow ? (
              <FlowCanvas flow={selectedFlow} templates={templates} API={API} clientId={user.clientId} onBack={()=>{setSelectedFlow(null); fetch(`${API}/flows?clientId=${user.clientId}`).then(r=>r.json()).then(d=>{ if(Array.isArray(d)) setFlows(d); }).catch(()=>{});}} onSaved={(nf)=>{ setFlows(flows.map(x=>x.id===nf.id?{...x,...nf}:x)); }} />
            ) : (
            <div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div style={s.pageTitle}>Flows</div>
                <button onClick={async()=>{
                  const name = prompt('Flow name?','Untitled Flow');
                  if(!name) return;
                  const res = await fetch(`${API}/flows`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({clientId:user.clientId,name})});
                  const nf = await res.json();
                  if(nf && nf.id) setFlows([nf,...flows]);
                }} style={{padding:'10px 18px',background:'#16a34a',color:'#fff',border:'none',borderRadius:'10px',cursor:'pointer',fontSize:'13px',fontWeight:'600'}}>+ New Flow</button>
              </div>
              <div style={s.card}>
                {flows.length===0 ? (
                  <div style={s.emptyBox}><div>No flows yet. Click "+ New Flow" to create one.</div></div>
                ) : (
                  <div>
                    {flows.map(f=>(
                      <div key={f.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 16px',borderBottom:'1px solid #eef2f0'}}>
                        <div>
                          <div style={{fontSize:'14px',fontWeight:'600',color:'#0f172a'}}>{f.name}</div>
                          <div style={{fontSize:'12px',color:'#64748b',marginTop:'2px'}}>Trigger: {f.trigger_type}{f.trigger_value?(' = '+f.trigger_value):''} · {f.status?'Active':'Off'}</div>
                        </div>
                        <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                        <button onClick={async()=>{ const ns=!f.status; const r=await fetch(`${API}/flows/${f.id}`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({clientId:user.clientId,status:ns})}); const d=await r.json(); if(d&&d.id){ setFlows(flows.map(x=>x.id===f.id?{...x,status:ns}:x)); } }} style={{padding:'6px 12px',background:f.status?'#dcfce7':'#f1f5f9',color:f.status?'#15803d':'#64748b',border:f.status?'1px solid #86efac':'1px solid #e2e8f0',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>{f.status?'\u25CF Active':'\u25CB Off'}</button>
                        <button onClick={()=>setSelectedFlow(f)} style={{padding:'6px 14px',background:'#16a34a',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>Open Builder</button>
                        <button onClick={async()=>{
                          if(!window.confirm('Delete this flow?')) return;
                          await fetch(`${API}/flows/${f.id}?clientId=${user.clientId}`,{method:'DELETE'});
                          setFlows(flows.filter(x=>x.id!==f.id));
                        }} style={{padding:'6px 12px',background:'rgba(239,68,68,0.1)',color:'#dc2626',border:'1px solid rgba(239,68,68,0.25)',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            )
          )}
          {page==='api-docs'&&(
            <div>
              <div style={s.pageTitle}>API Documentation</div>
              <div style={s.card}>
                <div style={{background:'#1e2139',borderRadius:'8px',padding:'16px',marginBottom:'12px'}}>
                  <div style={{fontSize:'12px',color:'#8b9dc3',marginBottom:'8px'}}>POST /send-message</div>
                  <pre style={{fontSize:'12px',color:'#34d399',margin:0}}>{`{\n  "phone": "966XXXXXXXXX",\n  "message": "Hello!",\n  "clientId": "${user.clientId}"\n}`}</pre>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}